<template>
	<div id="app" class="relative">

		<!-- Error/Success TOAST -->
		<div class="fixed inset-x-0 top-6 flex flex-col items-center space-y-2 z-[9999] pointer-events-none">
			<transition name="fade">
				<div v-if="errorMessage" class="max-w-md w-[90%] md:w-full text-center bg-red-100 border border-red-300 text-red-800 px-6 py-4 rounded-lg shadow-md pointer-events-auto">
					{{ errorMessage }}
				</div>
			</transition>
			<transition name="fade">
				<div v-if="successMessage" class="max-w-md w-[90%] md:w-full text-center bg-green-100 border border-green-300 text-green-800 px-6 py-4 rounded-lg shadow-md pointer-events-auto">
					{{ successMessage }}
				</div>
			</transition>
		</div>

		<!-- RESET PASSWORD PAGE -->
		<ResetPasswordPage v-if="!isLoggedIn && showResetPassword" :current-theme="currentTheme" :token="resetToken" :loading="authLoading" @reset-password="handleResetPassword" @cancel="showResetPassword = false" @validation-error="showErrorToast"/>

		<!-- LOGIN/SIGNUP -->
		<LoginSignUpPage v-else-if="!isLoggedIn && !showResetPassword" :currentTheme="currentTheme" :error-message="errorMessage" :success-message="successMessage" :loading="authLoading" @login="handleLogin" @signup="handleSignup" @forgot-password="handleForgotPassword" @validation-error="showErrorToast"/>

		<!-- PAIRING BEGIN -->
		<div v-else-if="isLoggedIn && !paired && !pairingStarted" :data-theme="currentTheme" class="lightTheme-bg fixed inset-0 flex items-center justify-center z-10 bg-[var(--bg-color)]">
			<PairingBegin 
			:callback="startPairing" :currentTheme="currentTheme"
			/>
		</div>

		<!-- PAIRING PROCESS -->
		<div  v-else-if="isLoggedIn && !paired && pairingStarted" :data-theme="currentTheme" class="lightTheme-bg fixed inset-0 flex items-center justify-center z-10">
			<PairingProcess 
			:callback="backToPair" :currentTheme="currentTheme" :qrDecode="onDecode" :qrInit="onInit" :percentSynced="percentSynced"
			/>
		</div>

			<!-- ACCOUNT PAGE  -->
			<div v-else-if="isLoggedIn && paired" :data-theme="currentTheme" class="md:min-h-screen min-h-[100vh] lightTheme-bg overflow-hidden flex items-center justify-center">
				<!-- ACCOUNT PAGE (default) -->
				<div v-if="currentView === 'account'" class="fixed inset-0 flex items-center justify-center z-10 bg-[var(--bg-color)]">
					<div class="relative h-[90vh] md:h-[90vh] w-[95%] md:w-[93%] lg:w-[85%] xl:w-[80%] mx-auto flex flex-col md:flex-row rounded-2xl shadow-2xl overflow-y-auto custom-scrollbar">
						<!-- SIDEBAR MENU -->
						<SidebarMenu
							:currentTheme="currentTheme" :btcOnly="btcOnly" :isMobileMenuOpen="isMobileMenuOpen" :chainDropdownOpen="chainDropdownOpen" :displayedChains="displayedChains" :selectedChain="selectedChain" @toggleMobileMenu="toggleMobileMenu" @closeMobileMenu="closeMobileMenu" @toggleChainDropdown="toggleChainDropdown" @selectChain="selectChain" @openAccountView="openAccountView" @backToOverview="openWalletView" @openTradeView="openTradeView" @showThemePopup="() => showThemePopup = true" @showResourcesPopup="() => showResourcesPopup = true" @showComingSoonMessage="showComingSoonMessage" @showExportZpubView="showExportZpubView" @unpairWallet="unpairWallet" @openSendView="startSend" @showReceiveView="showReceiveView"
						/>
						<div class="border-l border-[var(--border-color)] md:block hidden"></div>

						<!-- ACCOUNT DASHBOARD CONTENT -->
						<div class="w-full md:w-3/4 lg:w-4/5 h-full flex flex-col items-start pair-bg md:rounded-tr-2xl md:rounded-bl-none rounded-br-2xl rounded-bl-2xl z-10 p-6 overflow-y-auto custom-scrollbar">
							<div class="md:hidden flex flex-col bg-[var(--mobile-header-bg-color)] rounded-t-2xl w-full">
								<div class="flex justify-between items-center w-full mb-4">
									<div class="flex items-center space-x-2 px-4">
										<img :src="(currentTheme==='dark'||currentTheme==='navy') ? darkAccountIcon : lightAccountIcon" alt="Wallet" class="w-7 h-7"/>
										<span class="text-3xl font-archivomedium text-[var(--text-color)]">
											OTC Account
										</span>
									</div>
								</div>
							</div>

							<!-- DESKTOP HEADER -->
							<div class="hidden md:flex items-center justify-between w-full px-4 md:pt-10 md:px-2">
								<div class="flex items-center md:ml-0">
									<img :src="(currentTheme==='dark'||currentTheme==='navy') ? darkAccountIcon : lightAccountIcon" alt="Wallet Icon" class="w-6 h-6 md:w-8 md:h-8 mr-3"/>
									<h1 class="text-3xl md:text-4xl font-archivomedium text-[var(--text-color)]">
										OTC Account
									</h1>
								</div>
							</div>

							<!-- ACC DETAILS & KYC -->
							<div class="w-full md:mt-10 mt-4 animate-fade-in animate-scaleIn">
								<!-- ACCOUNT DROPDOWN (full width) -->
								<div class="relative transition-all duration-300">
									<!-- collapsed header -->
									<div class="bg-[var(--account-block-bg-color)] border border-[var(--border-color)] rounded-2xl shadow-md p-6 flex items-center justify-between cursor-pointer" @click="isProfileOpen = !isProfileOpen">
									<div class="flex items-center space-x-4">
										<img :src="`https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile.name || userProfile.email)}&background=252525&color=fcfcfc`" alt="Avatar" class="w-10 h-10 md:w-16 md:h-16 rounded-full ring-2 ring-[var(--accent-color)] object-cover"/>
										<div class="flex flex-col">
											<span class="md:text-xl text-md font-archivomedium text-[var(--text-color)]">
												{{ userProfile.name || userProfile.email }}
											</span>
											<span class="text-sm text-[var(--secondary-text-color)]">
												@{{ userProfile.username || 'username' }}
											</span>
										</div>
									</div>
									<div class="flex items-center space-x-3">
										<!-- only show edit when expanded -->
										<button v-if="isProfileOpen && !isEditingProfile" @click.stop="isEditingProfile = true" class="px-3 py-1 rounded-full border border-[var(--accent-color)] text-[var(--accent-color)] text-sm hover:bg-[var(--accent-color)] hover:text-white transition">
											Edit
										</button>
										<!--<svg :class="['w-6 h-6 transform transition-transform', isProfileOpen ? 'rotate-180' : '']" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" >
											<path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
										</svg>-->
										<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" :class="['w-6 h-6 transform transition-transform', isProfileOpen ? 'rotate-180' : '', (currentTheme==='dark' || currentTheme==='navy') ? 'text-white' : 'text-gray-700']">
											<path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
										</svg>

									</div>
								</div>

								<!-- expanded details -->
								<transition name="expand">
									<div v-if="isProfileOpen" class="mt-4 bg-[var(--account-block-bg-color)] border border-[var(--border-color)] rounded-xl shadow-md p-6 grid grid-cols-1 gap-6 overflow-hidden">
									<!-- top summary row -->
										<div class="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-6 pb-4 border-b border-[var(--border-color)]">
											<!-- KYC status badge -->
											<div class="flex flex-col">
												<span class="text-sm text-[var(--secondary-text-color)]">
													KYC Status
												</span>
												<span class="mt-1 inline-block px-3 py-1 rounded-full text-sm font-medium" :class="{
												'bg-[#F0F0F0] text-[#606060]': kycStatus==='Not Started',
												'bg-[#FFF4E5] text-[#CC7600]':   kycStatus==='Pending',
												'bg-[#E5FFE5] text-[#008000]':   kycStatus==='Approved',
												'bg-[#FFE5E5] text-[#CC0000]':   kycStatus==='Rejected'
												}">
													{{ statusEmoji[kycStatus] }} {{ kycStatus }}
												</span>
											</div>
											<!-- Total Deposits -->
											<div class="flex flex-col">
												<span class="text-sm text-[var(--secondary-text-color)]">
													Total Deposits
												</span>
												<span class="mt-1 px-2 py-1 bg-[var(--account-bg-block-color)] rounded text-[var(--account-block-text-color)]">
													{{ formatCurrency(totalDeposits) }}
												</span>
											</div>
											<!-- Company Name -->
											<div class="flex flex-col">
												<span class="text-sm text-[var(--secondary-text-color)]">
													Company Name
												</span>
												<span class="mt-1 px-2 py-1 bg-[var(--account-bg-block-color)] rounded text-[var(--account-block-text-color)]">
												{{ companyName }}
												</span>
											</div>
										</div>

										<!-- DISPLAY MODE -->
										<div v-if="!isEditingProfile" class="grid grid-cols-1 md:grid-cols-2 gap-6">
											<div class="flex flex-col">
												<span class="text-sm text-gray-400">
													Email
												</span>
												<span class="mt-1 px-2 py-1 bg-[var(--account-bg-block-color)] rounded text-[var(--account-block-text-color)]">
													{{ userProfile.email }}
												</span>
											</div>
											<div class="flex flex-col">
												<span class="text-sm text-gray-400">
													Name
												</span>
												<span class="mt-1 px-2 py-1 bg-[var(--account-bg-block-color)] rounded text-[var(--account-block-text-color)]">
													{{ userProfile.name || '—' }}
												</span>
											</div>
											<div class="flex flex-col">
												<span class="text-sm text-gray-400">
													Username
												</span>
												<span class="mt-1 px-2 py-1 bg-[var(--account-bg-block-color)] rounded text-[var(--account-block-text-color)]">
													{{ userProfile.username || '—' }}
												</span>
											</div>
										</div>

										<!-- EDIT MODE -->
										<form v-else @submit.prevent="updateProfile" class="grid grid-cols-1 md:grid-cols-2 gap-6">
											<div class="flex flex-col">
												<label class="text-sm text-[var(--text-color)] mb-2">
													Name:
												</label>
												<input v-model="userProfile.name" type="text" placeholder="Full Name" class="px-4 py-1 rounded-lg border border-[var(--border-color)]
												bg-[var(--account-bg-block-color)] text-[var(--account-block-text-color)] placeholder:text-[var(--secondary-text-color)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-color)]"/>
											</div>
											<div class="flex flex-col">
												<label class="text-sm text-[var(--text-color)] mb-2">Username:</label>
												<input v-model="userProfile.username" type="text" placeholder="Choose a Username" class="px-4 py-1 rounded-lg border border-[var(--border-color)] bg-[var(--account-bg-block-color)] text-[var(--account-block-text-color)] placeholder:text-[var(--secondary-text-color)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-color)]"/>
											</div>
											<div class="md:col-span-2 flex justify-end space-x-4 mt-4">
												<button type="submit" :disabled="loadingProfile" class="px-6 py-2 rounded-full bg-[var(--accent-color)] text-white font-archivomedium border border-[var(--accent-color)] hover:bg-transparent hover:text-[var(--accent-color)] transition disabled:opacity-50 disabled:cursor-not-allowed text-sm flex items-center">
													<span v-if="!loadingProfile">
														Save
													</span>
													<div v-else class="loader w-5 h-5"></div>
												</button>
												<button type="button" @click="isEditingProfile=false" :disabled="loadingProfile" class="px-6 py-2 rounded-full bg-gray-200 text-gray-700
												hover:bg-gray-300 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm">
													Cancel
												</button>
											</div>
										</form>
									</div>
								</transition>
							</div>


							<!-- Full-Width Action Buttons -->
							<div class="mt-8 w-full px-2 md:px-0">
								<div class="account-bg border border-[var(--border-color)] rounded-2xl shadow-lg p-6 flex flex-col lg:flex-row justify-around items-center gap-6">
									<div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
										<button @click="openWalletView" class="flex items-center space-x-3 px-5 py-3 border border-[var(--accent-color)] bg-[var(--accent-color)] text-white hover:bg-transparent hover:text-[var(--accent-color)] transition text-white rounded-xl hover:bg-opacity-90">
											<svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none"
											viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<path stroke-linecap="round" stroke-linejoin="round"
											d="M3 3v18h18M9 17V9m4 8V5m4 12v-4"/>
											</svg>
											<span class="font-archivomedium">
												Manage Assets
											</span>
										</button>
										<!-- Deposit ZAR -->
										<button @click="depositZAR" :disabled="kycStatus !== 'Approved'" class="flex items-center space-x-3 px-6 py-3 rounded-xl border border-[var(--accent-color)] bg-[var(--accent-color)] text-white hover:bg-transparent hover:text-[var(--accent-color)] transition disabled:opacity-50 disabled:cursor-not-allowed">
											<!-- Enhanced Cash Stack Icon -->
											<svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" viewBox="0 0 24 24" fill="none">
												<defs>
													<linearGradient id="billGrad" x1="0" y1="0" x2="0" y2="1">
														<stop offset="0%" stop-color="currentColor" stop-opacity="0.2"/>
														<stop offset="100%" stop-color="currentColor" stop-opacity="0.05"/>
													</linearGradient>
												</defs>
												<rect x="1" y="9" width="22" height="12" rx="2" fill="url(#billGrad)" stroke="currentColor" stroke-opacity="0.2" stroke-width="1"/>
												<rect x="1" y="6" width="22" height="12" rx="2" fill="url(#billGrad)" stroke="currentColor" stroke-opacity="0.4" stroke-width="1.5"/>
												<rect x="1" y="3" width="22" height="12" rx="2" fill="white" stroke="currentColor" stroke-width="2"/>
												<text x="12" y="9.5" text-anchor="middle" dominant-baseline="middle" font-size="7" font-weight="bold" fill="var(--accent-color)" font-family="Arial, sans-serif">
													R
												</text>
											</svg>
											<span class="font-archivomedium">
												OTC Trade
											</span>
										</button>
										<!-- Withdrawal Address -->
										<div>
											<!-- if chosen -->
											<button v-if="withdrawalAddress" @click="showFullWithdrawalModal = true" class="flex items-center space-x-3 px-6 py-3 rounded-xl border border-[var(--accent-color)] bg-[var(--accent-color)] text-white hover:bg-transparent hover:text-[var(--accent-color)] transition disabled:opacity-50 disabled:cursor-not-allowed">
												<svg xmlns="http://www.w3.org/2000/svg" class="md:w-10 md:h-10 w-6 h-6" fill="none"
												viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
													<path stroke-linecap="round" stroke-linejoin="round"
												d="M5 3v16a2 2 0 002 2h10a2 2 0 002-2V3M8 7h8M8 11h8M8 15h8" />
												</svg>
												<span class="font-archivamedium">
													Withdrawal Address – {{ abbr(withdrawalAddress.address) }}
												</span>
											</button>
											<!-- if not chosen -->
											<button v-else @click="showWithdrawalAddressPopup = true" :disabled="kycStatus !== 'Approved'" class="flex items-center space-x-3 px-6 py-3 rounded-xl border border-[var(--accent-color)] bg-[var(--accent-color)] text-white hover:bg-transparent hover:text-[var(--accent-color)] transition disabled:opacity-50 disabled:cursor-not-allowed">
												<svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none"
												viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
													<path stroke-linecap="round" stroke-linejoin="round"
												d="M5 3v16a2 2 0 002 2h10a2 2 0 002-2V3M8 7h8M8 11h8M8 15h8" />
												</svg>
												<span class="font-archivamedium">
													Withdrawal Address
												</span>
											</button>
										</div>
										<!-- Trade in DeFi -->
										<button @click="handleTradeClick" class="flex items-center space-x-3 px-6 py-3 rounded-xl border border-[var(--accent-color)] bg-[var(--accent-color)] text-white hover:bg-transparent hover:text-[var(--accent-color)] transition">
											<svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6" fill="none"
											viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
												<polyline points="3 17 9 11 13 15 21 7" stroke-linecap="round" stroke-linejoin="round"/>
											</svg>
											<span class="font-archivomedium">
												Trade in DeFi
											</span>
										</button>
									</div>
								</div>
							</div>

							<!-- KYC BLOCK (full width under account) -->
							<div class="xl:w-[40%] lg:w-[50%] md:w-[60%] w-full mt-2 rounded-2xl p-4">
										<h2 class="text-lg font-archivomedium text-[var(--text-color)] my-2">
											KYC Details
										</h2>
										<!-- Status Header -->
										<div class="flex items-center h-16 rounded-lg px-6" :class="{
											'bg-gray-400 border-l-4 border-gray-300': kycStatus === 'Not Started',
											'bg-yellow-800 border-l-4 border-yellow-700': kycStatus === 'Pending',
											'bg-green-600 border-l-4 border-green-600': kycStatus === 'Approved',
											'bg-red-600 border-l-4 border-red-500': kycStatus === 'Rejected'
											}" :style="{
											background:
											kycStatus === 'Not Started'
											? 'linear-gradient(90deg, rgba(246,248,250,0.7) 0%, rgba(232,236,240,1) 50%, rgba(246,248,250,0.7) 100%)'
											: kycStatus === 'Pending'
											? 'linear-gradient(90deg, rgba(255,241,224,0.7) 0%, rgba(255,210,164,0.7) 50%, rgba(255,180,120,0.7) 100%)'
											: kycStatus === 'Approved'
											? 'linear-gradient(90deg, rgba(204,255,204,0.7) 0%, rgba(160,230,160,0.9) 50%, rgba(120,200,120,0.6) 100%)'
											: 'linear-gradient(90deg, rgba(255,220,220,0.7) 0%, rgba(255,170,170,0.7) 50%, rgba(255,120,120,0.7) 100%)'
											}"
										>
											<!-- kyc card -->
											<svg class="w-8 h-8 mr-3 flex-shrink-0" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
												<rect x="8" y="16" width="48" height="32" rx="4" fill="white" stroke="currentColor" stroke-width="2"/>
												<circle cx="20" cy="32" r="6" fill="currentColor" opacity=".2"/>
												<path d="M32 28h16M32 36h16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
											</svg>

											<!-- status -->
											<span class="font-medium text-lg" :class="{
											'text-gray-700':  kycStatus === 'Not Started',
											'text-yellow-800': kycStatus === 'Pending',
											'text-green-800': kycStatus === 'Approved',
											'text-red-800':   kycStatus === 'Rejected'
											}">
												{{ statusEmoji[kycStatus] }} {{ kycStatus }}
											</span>
										</div>

										<!-- Actions -->
										<div v-if="kycStatus !== 'Approved' && kycStatus !== 'Pending'" class="px-5 py-6 flex flex-col space-y-3 bg-transparent">
											<button v-if="kycStatus === 'Not Started'" @click="showKycForm = true" class="w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-full bg-[var(--accent-color)] text-white hover:opacity-90 transition">
												<span>
													KYC Now to Deposit
												</span>
												<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
													<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
												d="M9 5l7 7-7 7" />
												</svg>
											</button>

											<!-- <div v-else-if="kycStatus === 'Pending'" class="flex items-center justify-center space-x-2 px-4 py-2 rounded-full bg-gray-300 text-gray-600 cursor-not-allowed">
												KYC Pending
											</div> -->

											<div v-else-if="kycStatus === 'Rejected'" class="w-full flex flex-col space-y-4">
												<button @click="showRejectionReason = true" class="w-full px-4 py-2 rounded-full border border-red-400 text-red-600 hover:bg-red-50 transition" >
													View Rejection Reason
												</button>
												<button @click="showKycForm = true" class="w-full px-4 py-2 rounded-full bg-red-400 text-white hover:opacity-90 transition">
													Resubmit KYC
												</button>
											</div>

											<!--<div v-else-if="kycStatus === 'Approved'" class="w-full text-center px-4 py-2 rounded-full bg-green-200 text-green-800 cursor-default">
												KYC Approved ✓
											</div> -->
										</div>
									</div>
								</div>


							<!-- Recent Operations Table -->
							<div class="w-full mt-10">
								<h3 class="text-xl font-archivomedium text-[var(--text-color)] mb-4">
									Recent Operations
								</h3>
								<div class="overflow-x-auto">
									<table class="min-w-full table-auto account-table-bg rounded-lg overflow-hidden">
										<thead class="text-[var(--text-color)] bg-[var(--myassets-bg-color)] rounded-t-lg overflow-hidden">
											<tr>
												<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Date</th>
												<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Time (UTC)</th>
												<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Type</th>
												<th class="px-4 py-2 text-left text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Amount</th>
												<th class="px-4 py-2 text-right text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Withdrawal Address</th>
												<th class="px-4 py-2 text-center text-xs font-archivosemibold text-[var(--secondary-text-color)] uppercase">Status</th>
											</tr>
										</thead>
										<tbody class="text-[var(--text-color)] font-archivolight text-sm">
											<tr v-if="operations.length === 0">
												<td colspan="5" class="px-4 py-6 text-center text-gray-500">No operations… yet</td>
											</tr>
											<tr v-for="(op, i) in operations" :key="i" class="border-t border-[var(--border-color)]">
												<td class="px-4 py-2">
													{{ op.date }}
												</td>
												<td class="px-4 py-2">
													{{ op.time }}
												</td>
												<td class="px-4 py-2">
													{{ op.type }}
												</td>
												<td class="px-4 py-2">
													<span v-if="op.status==='Pending'">Awaiting...</span>
													<span v-else-if="op.status==='Rejected'">—</span>
													<span v-else>{{ formatCurrency(op.amount)  }}</span>
												</td>
												<td class="px-4 py-2 text-right relative group">
													<span class="truncate block max-w-xs">
														{{ op.withdrawalAddress.slice(0,6) + '...' + op.withdrawalAddress.slice(-4) }}
													</span>
													<div class="absolute left-0 bottom-full mb-1 hidden group-hover:block bg-[var(--dropdown-tx-history-bg-color)] text-[var(--text-color)] text-xs px-3 py-2 rounded shadow-lg whitespace-nowrap z-10">
														{{ op.withdrawalAddress }}
													</div>
												</td>
												<td class="px-4 py-2 text-center">
													<span
														:class="[
														'inline-block px-3 py-1 rounded-full text-xs font-medium',
														op.status === 'Pending'
														? 'bg-[#FFD5AE] text-[#EC5900]'
														: op.status === 'Approved'
														? 'bg-[#9FFFCA] text-[#00671D]'
														/* Rejected */
														: 'bg-[#FFE5E5] text-[#CC0000]'
														]"
													>
													<!--<span :class="['inline-block px-3 py-1 rounded-full text-xs font-medium', op.status === 'Pending' ? 'bg-[#FFD5AE] text-[#EC5900]' : 'bg-[#9FFFCA] text-[#00671D]']">-->
														{{ op.status }}
													</span>
												</td>
											</tr>
										</tbody>
									</table>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- WALLET OVERVIEW -->
				<div v-if="currentView === 'wallet' && !sending && !showReceive" class="fixed inset-0 flex items-center justify-center z-10 bg-[var(--bg-color)]">
					<div class="relative h-[90vh] md:h-[90vh] w-[95%] md:w-[93%] lg:w-[85%] xl:w-[80%] mx-auto flex flex-col md:flex-row rounded-2xl shadow-2xl">

					<!-- SIDEBAR MENU-->
					<SidebarMenu 
						:currentTheme="currentTheme" :btcOnly="btcOnly" :isMobileMenuOpen="isMobileMenuOpen" :highlightChain="highlightChain" :chainDropdownOpen="chainDropdownOpen" :displayedChains="displayedChains" :selectedChain="selectedChain" @toggleMobileMenu="toggleMobileMenu" @closeMobileMenu="closeMobileMenu" @toggleChainDropdown="toggleChainDropdown" @selectChain="selectChain" @backToOverview="backToOverview" @openTradeView="openTradeView" @openAccountView="openAccountView" @showThemePopup="() => showThemePopup = true" @showResourcesPopup="() => showResourcesPopup = true" @showComingSoonMessage="showComingSoonMessage" @showExportZpubView="showExportZpubView" @unpairWallet="unpairWallet" @openSendView="startSend" @showReceiveView="showReceiveView"
					/>
					<div class="border-l border-[var(--border-color)] md:block hidden"></div>
					<walletOverview
						:currentTheme="currentTheme"
						:btcOnly="btcOnly"
						:selectedChain="selectedChain"
						:selectedAccount="selectedAccount"
						:shortAddress="shortAddress"
						:showUnconf="showUnconf"
						:unconfirmedBalance="unconfirmedBalance"
						:walletAccounts="walletAccounts"
						:accountDropdownOpen="accountDropdownOpen"
						:tokenContractDropdownOpen="tokenContractDropdownOpen"
						:chainDropdownOpen="chainDropdownOpen"
						:displayedChains="displayedChains"
						:assetList="assetList"
						:showBalance="showBalance"
						:displayedBalance="displayedBalance"
						:balanceDigits="balanceDigits"
						:priceDigits="priceDigits"
						:balanceLabel="balanceLabel"
						:showPortfolioUSD="showPortfolioUSD"
						:portfolioValue="portfolioValue"
						:assetMenuOpen="assetMenuOpen"
						:explorerIcon="explorerIcon"
						:activeTab="activeTab"
						:switchingChain="switchingChain"
						:isSyncing="isSyncing"
						:isBalanceLoading="isBalanceLoading"
						:recentTransactions="recentTransactions"
						:allTransactions="allTransactions"
						@update:activeTab="val => activeTab = val"
						@startSync="startSync"
						@startSend="startSend"
						@showReceiveView="showReceiveView"
						@toggleBalance="toggleBalance"
						@togglePortfolioUSD="showPortfolioUSD = !showPortfolioUSD"
						@toggleAccountDropdown="toggleAccountDropdown"
						@toggleChainDropdown="toggleChainDropdown"
						@toggleTokenContractDropdown="toggleTokenContractDropdown"
						@selectAccount="selectAccount"
						@openAddTokenPopup="openAddTokenPopup"
						@openAddAccountPopup="openAddAccountPopup"
						@copyWalletAddress="copyWalletAddress"
						@openBlockExplorer="openBlockExplorer"
						@openAssetMenu="openAssetMenu"
						@onSelectSend="onSelectSend"
						@onSelectReceive="onSelectReceive"
						@onSelectRemove="onSelectRemove"
					/>
				</div>
			</div>

			<!-- RECEIVE SCREEN -->
			<div v-else-if="showReceive" class="fixed inset-0 flex items-center justify-center z-10 bg-[var(--bg-color)]">
				<div class="relative h-[90vh] md:h-[90vh] w-[95%] md:w-[93%] lg:w-[85%] xl:w-[80%] mx-auto flex flex-col md:flex-row rounded-2xl shadow-2xl">
					<SidebarMenu 
					:currentTheme="currentTheme" :btcOnly="btcOnly" :isMobileMenuOpen="isMobileMenuOpen" :chainDropdownOpen="chainDropdownOpen" :highlightChain="highlightChain" :displayedChains="displayedChains" :selectedChain="selectedChain" @toggleMobileMenu="toggleMobileMenu" @closeMobileMenu="closeMobileMenu" @toggleChainDropdown="toggleChainDropdown" @selectChain="selectChain" @backToOverview="backToOverview" @openTradeView="openTradeView" @openAccountView="openAccountView" @showThemePopup="() => showThemePopup = true" @showResourcesPopup="() => showResourcesPopup = true" @showComingSoonMessage="showComingSoonMessage" @showExportZpubView="showExportZpubView" @unpairWallet="unpairWallet" @openSendView="startSend" @showReceiveView="showReceiveView"
					/>
				<div class="border-l border-[var(--border-color)] md:block hidden"></div>
					<ReceiveAsset 
					:currentTheme="currentTheme" :selectedChain="selectedChain" :selectedAssetObject="selectedAssetObject" :walletAddress="walletAddress" @backToOverview="backToOverview" @copyWalletAddress="copyWalletAddress"
					/>
				</div>
			</div>

			<!-- SEND SCREEN -->
			<div v-else-if="sending && !psbtGenerated" class="fixed inset-0 flex items-center justify-center z-10 bg-[var(--bg-color)]">
				<div class="relative h-[90vh] md:h-[90vh] w-[95%] md:w-[93%] lg:w-[85%] xl:w-[80%] mx-auto flex flex-col md:flex-row rounded-2xl shadow-2xl">
					<SidebarMenu 
					:currentTheme="currentTheme" :btcOnly="btcOnly" :isMobileMenuOpen="isMobileMenuOpen" :chainDropdownOpen="chainDropdownOpen" :highlightChain="highlightChain" :displayedChains="displayedChains" :selectedChain="selectedChain" @toggleMobileMenu="toggleMobileMenu" @closeMobileMenu="closeMobileMenu" @toggleChainDropdown="toggleChainDropdown" @selectChain="selectChain" @backToOverview="backToOverview" @openTradeView="openTradeView" @openAccountView="openAccountView" @showThemePopup="() => showThemePopup = true" @showResourcesPopup="() => showResourcesPopup = true" @showComingSoonMessage="showComingSoonMessage" @showExportZpubView="showExportZpubView" @unpairWallet="unpairWallet" @openSendView="startSend" @showReceiveView="showReceiveView"
					/>        
					<div class="border-l border-[var(--border-color)] md:block hidden"></div>
					<SendAsset 
					:currentTheme="currentTheme" :btcOnly="btcOnly" :selectedChain="selectedChain" :selectedAssetObject="selectedAssetObject" :availableUsd="availableUsd" :amountUsd="amountUsd" :assetList="assetList" :walletAccounts="walletAccounts" :selectedAccount="selectedAccount" :recipientAddress="recipientAddress" :amountInput="amountInput" :currentFee="currentFee" :feeUsdPrice="feeUsdPrice" :selectedFee="selectedFee" :assetDropdownOpen="assetDropdownOpen" :addressMenuOpen="addressMenuOpen" :sendAccountDropdownOpen="sendAccountDropdownOpen" :txTypeStr="walletConnectMethod" @update:recipientAddress="val => recipientAddress = val" @update:amountInput="val => amountInput = val" @update:addressMenuOpen="val => addressMenuOpen = val" @update:sendAccountDropdownOpen="val => sendAccountDropdownOpen = val" @toggleAssetDropdown="toggleAssetDropdown" @selectAsset="selectAsset" @toggleAddressMenu="toggleAddressMenu" @toggleAddressBookPopup="toggleAddressBookPopup" @toggleSendAccountDropdown="toggleSendAccountDropdown" @selectSendAccount="selectSendAccount" @startScanAddress="startScanAddress" @setMaxAmount="setMaxAmount" @handleFirstFee="handleFirstFee" @adjustFeeRate="adjustFeeRate" @generatePSBT="generatePSBT"  @backToOverview="backToOverview"
					/>
				</div>    
			</div>

			<!-- SCAN QR CODE (SENDING) -->
			<div v-if="isScanningAddress">
				<ScanQRCodeSend 
				:currentTheme="currentTheme" :selectedChain="selectedChain" @stop-scan="stopScanAddress" @decode="onDecodeAddress" @init="onInit"
				/>
			</div>

			<!-- DISPLAY PSBT QR CODE -->
			<div v-else-if="sending && psbtGenerated && !signed" class="fixed inset-0 flex items-center justify-center z-10 bg-[var(--bg-color)]">
				<div class="relative h-[90vh] md:h-[90vh] w-[95%] md:w-[93%] lg:w-[85%] xl:w-[80%] mx-auto flex flex-col md:flex-row rounded-2xl shadow-2xl">
					<SidebarMenu 
					:currentTheme="currentTheme" :btcOnly="btcOnly" :isMobileMenuOpen="isMobileMenuOpen" :chainDropdownOpen="chainDropdownOpen" :highlightChain="highlightChain" :displayedChains="displayedChains" :selectedChain="selectedChain" @toggleMobileMenu="toggleMobileMenu" @closeMobileMenu="closeMobileMenu" @toggleChainDropdown="toggleChainDropdown" @selectChain="selectChain" @backToOverview="backToOverview" @openTradeView="openTradeView" @openAccountView="openAccountView" @showThemePopup="() => showThemePopup = true" @showResourcesPopup="() => showResourcesPopup = true" @showComingSoonMessage="showComingSoonMessage" @showExportZpubView="showExportZpubView" @unpairWallet="unpairWallet" @openSendView="startSend" @showReceiveView="showReceiveView"
					/>
					<div class="border-l border-[var(--border-color)] md:block hidden"></div>
					<DisplayPsbt :currentTheme="currentTheme" :selectedChain="selectedChain" :selectedAsset="selectedAsset" :qrIndex="qrIndex" :chunksLength="chunks.length" :qrCodeUrl="qrCodeUrl" :transactionDetails="transactionDetails" :showFullUnsignedTx="showFullUnsignedTx" :txTypeStr="walletConnectMethod" :isApprove="isApprove" :isSwap="isSwap" :isSign="isSign" @prev-qr="prevQR" @next-qr="nextQR" @next-broadcast="nextBroadcast" @backToOverview="backToOverview" @backToSend="psbtGenerated = false" @copy-unsigned-hash="copyUnsignedTxHash" @toggle-full-unsigned="toggleFullTx" @copy-unsigned-hex="copyUnsignedTx" @open-raw-decoder="openRawTransactionDecoder"
					/>
				</div>
			</div>

			<!-- SCAN SIGNED TRANSACTION -->
			<div v-else-if="sending && signed && !transactionComplete" class="fixed inset-0 flex items-center justify-center z-10 bg-[var(--bg-color)]">
				<div class="relative h-[90vh] md:h-[90vh] w-[95%] md:w-[93%] lg:w-[85%] xl:w-[80%] mx-auto flex flex-col md:flex-row rounded-2xl shadow-2xl">
					<SidebarMenu 
					:currentTheme="currentTheme" :btcOnly="btcOnly" :isMobileMenuOpen="isMobileMenuOpen" :chainDropdownOpen="chainDropdownOpen" :highlightChain="highlightChain" :displayedChains="displayedChains" :selectedChain="selectedChain" @toggleMobileMenu="toggleMobileMenu" @closeMobileMenu="closeMobileMenu" @toggleChainDropdown="toggleChainDropdown" @selectChain="selectChain" @backToOverview="backToOverview" @openTradeView="openTradeView" @openAccountView="openAccountView" @showThemePopup="() => showThemePopup = true" @showResourcesPopup="() => showResourcesPopup = true" @showComingSoonMessage="showComingSoonMessage" @showExportZpubView="showExportZpubView" @unpairWallet="unpairWallet" @openSendView="startSend" @showReceiveView="showReceiveView"
					/>    
					<div class="border-l border-[var(--border-color)] md:block hidden"></div>
					<ScanSignedTransaction 
					:currentTheme="currentTheme" :selectedChain="selectedChain" :percentSynced="percentSynced" :isApprove="isApprove" :isSwap="isSwap" :isSign="isSign" @decode="onDecode" @init="onInit" @back-to-overview="backToOverview" @back-to-psbt="backToPSBT"
					/>
				</div>
			</div>

			<!-- TRADE SCREEN -->
			<div v-else-if="showTradeView" class="fixed inset-0 flex items-center justify-center z-10 bg-[var(--bg-color)]">
				<div class="relative h-[90vh] md:h-[90vh] w-[95%] md:w-[93%] lg:w-[85%] xl:w-[80%] mx-auto flex flex-col md:flex-row rounded-2xl">
					<SidebarMenu 
					:currentTheme="currentTheme" :btcOnly="btcOnly" :isMobileMenuOpen="isMobileMenuOpen" :chainDropdownOpen="chainDropdownOpen" :highlightChain="highlightChain" :displayedChains="displayedChains" :selectedChain="selectedChain" @toggleMobileMenu="toggleMobileMenu" @closeMobileMenu="closeMobileMenu" @toggleChainDropdown="toggleChainDropdown" @selectChain="selectChain" @backToOverview="backToOverview" @openTradeView="openTradeView" @openAccountView="openAccountView" @showThemePopup="() => showThemePopup = true" @showResourcesPopup="() => showResourcesPopup = true" @showComingSoonMessage="showComingSoonMessage" @showExportZpubView="showExportZpubView" @unpairWallet="unpairWallet" @openSendView="startSend" @showReceiveView="showReceiveView"
					/>
					<div class="border-l border-[var(--border-color)] md:block hidden"></div>
					<TradeView 
					:currentTheme="currentTheme" :supportedDApps="supportedDApps" :walletConnectUri="walletConnectUri" :btcOnly="btcOnly" :isMobileMenuOpen="isMobileMenuOpen" :chainDropdownOpen="chainDropdownOpen" :displayedChains="displayedChains" :wcSessions="wcSessions" :selectedChain="selectedChain" @update:walletConnectUri="val => walletConnectUri = val" @startScanWC="startWalletConnectQRAddress"  @connectWC="connectWalletConnect" @disconnectWC="disconnectWalletConnect" @disconnectAllWC="disconnectAllWalletConnect" @backToOverview="backToOverview" @toggleMobileMenu="toggleMobileMenu" @closeMobileMenu="closeMobileMenu" @toggleChainDropdown="toggleChainDropdown" @selectChain="selectChain" @openTradeView="openTradeView" @showThemePopup="() => showThemePopup = true" @showResourcesPopup="() => showResourcesPopup = true" @showComingSoonMessage="showComingSoonMessage" @showExportZpubView="showExportZpubView" @unpairWallet="unpairWallet"
					/>
				</div>
			</div>

			<!-- WALLET CONNECT QR SCANNING -->
			<div v-if="showWCScanner">
				<WCScanner 
				:currentTheme="currentTheme" @decode="onWCDecode" @init="onInit" @stopWCScanner="stopWCScanner"
				/>
			</div>

			<!-- EXPORT ZPUB SCREEN -->
			<div v-else-if="exportZpubView" class="fixed inset-0 flex items-center justify-center z-10 bg-[var(--bg-color)]">
				<div class="relative h-[90vh] md:h-[90vh] w-[95%] md:w-[93%] lg:w-[85%] xl:w-[80%] mx-auto flex flex-col md:flex-row rounded-2xl">
					<SidebarMenu 
					:currentTheme="currentTheme" :btcOnly="btcOnly" :isMobileMenuOpen="isMobileMenuOpen" :chainDropdownOpen="chainDropdownOpen" :highlightChain="highlightChain" :displayedChains="displayedChains" :selectedChain="selectedChain" @toggleMobileMenu="toggleMobileMenu" @closeMobileMenu="closeMobileMenu" @toggleChainDropdown="toggleChainDropdown" @selectChain="selectChain" @backToOverview="backToOverview" @openTradeView="openTradeView" @openAccountView="openAccountView" @showThemePopup="() => showThemePopup = true" @showResourcesPopup="() => showResourcesPopup = true" @showComingSoonMessage="showComingSoonMessage" @showExportZpubView="showExportZpubView" @unpairWallet="unpairWallet" @openSendView="startSend" @showReceiveView="showReceiveView"
					/>           
					<div class="border-l border-[var(--border-color)] md:block hidden"></div>
					<ExportZpub 
					:currentTheme="currentTheme" :zpub="zpub" @copyZpub="copyZpub" @backToOverview="backToOverview"
					/>
				</div>
			</div>

			<!-- TRANSACTION COMPLETE -->
			<div v-else-if="transactionComplete">
				<TransactionCompleteScreen 
				:currentTheme="currentTheme" :txidStr="txidStr" :txTypeStr="walletConnectMethod" @viewInExplorer="openTxInExplorer" @done="backToWallet"
				/>
			</div>

			<!-- TRANSACTION HISTORY SCREEN -->
			<div v-else-if="showHistory" class="fixed inset-0 flex items-center justify-center z-10 bg-[var(--bg-color)]">
				<div class="relative h-[90vh] md:h-[90vh] w-[95%] md:w-[93%] lg:w-[85%] xl:w-[80%] mx-auto flex flex-col md:flex-row rounded-2xl">
					<SidebarMenu 
					:currentTheme="currentTheme" :btcOnly="btcOnly" :isMobileMenuOpen="isMobileMenuOpen" :chainDropdownOpen="chainDropdownOpen" :highlightChain="highlightChain" :displayedChains="displayedChains" :selectedChain="selectedChain" @toggleMobileMenu="toggleMobileMenu" @closeMobileMenu="closeMobileMenu" @toggleChainDropdown="toggleChainDropdown" @selectChain="selectChain" @backToOverview="backToOverview" @openTradeView="openTradeView" @openAccountView="openAccountView" @showThemePopup="() => showThemePopup = true" @showResourcesPopup="() => showResourcesPopup = true" @showComingSoonMessage="showComingSoonMessage" @showExportZpubView="showExportZpubView" @unpairWallet="unpairWallet" @openSendView="startSend" @showReceiveView="showReceiveView"
					/>    
					<div class="border-l border-[var(--border-color)] md:block hidden"></div>
					<TransactionHistory
						:currentTheme="currentTheme"
						:transactions="allTransactions"
						:explorer-url="blockExplorerUrl"
						:selectedChain="selectedChain"
						@copy-address="handleCopyAddress"
						@copy-hash="copyHash"
						@backToOverview="backToOverview"
					/>
				</div>
			</div>


			<!-- ADD TOKEN CONTRACT POPUP -->
			<div v-if="showAddTokenPopup">
				<AddTokenPopup 
				:currentTheme="currentTheme" :selectedChain="selectedChain" :tokenPopupMode="tokenPopupMode" :tokenAddress="tokenAddress" :tokenDecimals="tokenDecimals" :tokenTicker="tokenTicker" :tokenName="tokenName" :isValidating="isValidating" :defaultTokensImported="defaultTokensImported" @cancel="cancelAddTokenPopup" @import-preferences="importTokenPreferences" @validate="validateTokenContract" @update:tokenPopupMode="val => tokenPopupMode = val" @update:tokenAddress="val => tokenAddress = val" @update:tokenDecimals="val => tokenDecimals = val" @update:tokenTicker="val => tokenTicker = val" @update:tokenName="val => tokenName = val"
				/>
			</div>

			<!-- THEME POPUP -->
			<div v-if="showThemePopup">
				<ThemeSelectionPopup 
				:currentTheme="currentTheme" @update:currentTheme="val => currentTheme = val" @close="showThemePopup = false"
				/>
			</div>

			<!-- RESOURCES POPUP -->
			<div v-if="showResourcesPopup">
				<ResourcesPopup 
				:currentTheme="currentTheme" @close="showResourcesPopup = false" 
				/>
			</div>

			<!-- ADD NETWORK POPUP -->
			<div v-if="showAddNetworkPopup">
				<AddNetworkPopup 
				:currentTheme="currentTheme" :availableNetworks="availableNetworks" @close="closeNetworkPopup" @add-network="addNetwork"
				/>
			</div>

			<!-- ADD ACCOUNT POPUP -->
			<div v-if="showAddAccountPopup">
				<AddAccountPopup 
				:currentTheme="currentTheme" :newAccountName="newAccountName" :accountCount="walletAccounts[selectedChain.name]?.length || 0" @close="showAddAccountPopup = false" @update:newAccountName="val => newAccountName = val" @add-account="addAccount"
				/>
			</div>

			<!-- REMOVE TOKEN ASSET -->
			<div v-if="showRemoveConfirm">
				<RemoveTokenAsset :currentTheme="currentTheme" @confirm="confirmRemove" @cancel="cancelRemove"
				/>
			</div>

			<!-- ADDRESSBOOK POPUP -->
			<div  v-if="showAddressBookPopup">
				<AddressBookPopup :currentTheme="currentTheme" :addressBook="addressBook" :addingContact="addingContact" :newContactName="newContactName" :newContactAddress="newContactAddress" @close="toggleAddressBookPopup" @select-entry="selectBookAddress" @start-add="addingContact = true" @update:newContactName="val => newContactName = val" @update:newContactAddress="val => newContactAddress = val" @add-address="addAddress"
				/>
			</div>

			<!-- COMING SOON POPUP -->
			<div v-if="showComingSoonPopup" @click.self="showComingSoonPopup = false" class="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-20">
				<div class="popup-bg text-[var(--popup-text-color)] rounded-lg px-8 py-12 w-10/12 md:w-3/6 lg:w-1/3 xl:w-1/4 relative brightness-110">
					<button @click="showComingSoonPopup = false" class="absolute top-4 right-4">
						<img :src="(currentTheme === 'dark' || currentTheme === 'navy') ? darkX : lightX" alt="Close Icon" class="w-3 h-3"/>
					</button>
					<h2 class="md:text-3xl text-2xl font-archivomedium mb-4 text-center text-[var(--popup-text-color)]">
						Coming Soon
					</h2>
					<p class="text-center font-archivoregular text-[var(--popup-text-color)]">
						Follow our <a href="https://x.com/CardwareWallet" class="underline text-[#FF6000]" target="_blank">socials</a> for more information
					</p>
				</div>
			</div>

			<!-- INFO TOAST POPUP -->
			<div v-if="infoMessage" class="fixed top-10 left-1/2 -translate-x-1/2 w-4/5 md:top-12 md:right-20 md:left-auto md:w-auto md:translate-x-0 lg:top-12 lg:right-44 z-[9999] flex items-center space-x-3 bg-blue-100 border border-blue-300 shadow-xl shadow-blue-500/10 rounded-md py-6 px-8 transition-transform ease-in-out duration-200">
				<svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 1010 10A10 10 0 0012 2z" />
				</svg>
				<p class="font-archivoregular text-blue-800">
					{{ infoMessage }}
				</p>
			</div>

			<!-- SUCCESS TOAST POPUP 
			<div v-if="successMessage" class="fixed top-10 left-1/2 -translate-x-1/2 w-4/5 md:top-12 md:right-20 md:left-auto md:w-auto md:translate-x-0 lg:top-12 lg:right-44 z-[9999] flex items-center space-x-3 bg-green-100 border border-green-300 shadow-xl shadow-green-500/10 rounded-md py-6 px-8 transition-transform ease-in-out duration-200">
				<svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
				</svg>
				<p class="font-archivoregular text-green-800">
					{{ successMessage }}
				</p>
			</div>

			<!- ERROR TOAST POPUP ->
			<div v-if="errorMessage" class="fixed top-10 left-1/2 -translate-x-1/2 w-4/5 md:top-10 md:right- md:left-auto md:w-auto md:translate-x-0 lg:top-8 lg:right-44 z-[9999] flex items-center space-x-3 bg-red-100 border border-red-300 shadow-xl shadow-red-500/10 rounded-md py-6 px-8 transition-transform ease-in-out duration-200">
				<svg class="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
					<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
				</svg>
				<p class="font-archivoregular text-red-800">
					{{ errorMessage }}
				</p>
			</div>-->


			<!-- SHOW CHOSEN WITHDRAWAL ADDY -->
			<transition name="fade-in">
				<div v-if="showFullWithdrawalModal" class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4" @click.self="showFullWithdrawalModal = false" >
					<div class="popup-bg rounded-xl shadow-2xl w-full max-w-md p-6 relative animate-scale-in overflow-hidden">
						<!-- close -->
						<button @click="showFullWithdrawalModal = false" class="absolute top-4 right-4 p-2 rounded-full hover:bg-[var(--hover-bg-color)] transition">
							<img :src="(currentTheme==='dark'||currentTheme==='navy') ? darkX : lightX" alt="Close" class="w-4 h-4"/>
						</button>

						<div class="flex flex-col items-center space-y-6">
							<!-- animated icon -->
							<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" stroke="var(--accent-color)" fill="var(--accent-color)" stroke-linecap="round" stroke-linejoin="round" stroke-width="2">
								<g fill="none">
									<path fill-opacity="0" stroke-dasharray="64" stroke-dashoffset="64" d="M22 3v14c0 0.55 -0.45 1 -1 1h-14c-0.55 0 -1 -0.45 -1 -1v-14c0 -0.55 0.45 -1 1 -1h14c0.55 0 1 0.45 1 1Z">
										<animate fill="freeze" attributeName="fill-opacity" begin="1.9s" dur="0.15s" values="0;0.3" />
										<animate fill="freeze" attributeName="stroke-dashoffset" dur="0.6s" values="64;0" />
									</path>
									<path stroke-dasharray="10" stroke-dashoffset="10" d="M10 6h8">
										<animate fill="freeze" attributeName="stroke-dashoffset" begin="0.7s" dur="0.2s" values="10;0"/>
									</path>
									<path stroke-dasharray="10" stroke-dashoffset="10" d="M10 10h8">
										<animate fill="freeze" attributeName="stroke-dashoffset" begin="0.9s" dur="0.2s" values="10;0"/>
									</path>
									<path stroke-dasharray="6" stroke-dashoffset="6" d="M10 14h5">
										<animate fill="freeze" attributeName="stroke-dashoffset" begin="1.1s" dur="0.2s" values="6;0"/>
									</path>
									<path stroke-dasharray="36" stroke-dashoffset="36" d="M2 6v15c0 0.55 0.45 1 1 1h15">
										<animate fill="freeze" attributeName="stroke-dashoffset" begin="1.4s" dur="0.5s" values="36;0"/>
									</path>
								</g>
							</svg>

							<h2 class="text-2xl font-archivomedium text-[var(--text-color)] text-center">
								Your Withdrawal Address
							</h2>

							<!-- address block -->
							<div class="w-full bg-[var(--account-bg-block-color)] border border-[var(--border-color)] rounded-lg p-4 flex items-center justify-between" >
								<code class="font-mono text-sm text-[var(--accent-color)] break-all">
									{{ withdrawalAddress.address }}
								</code>
								<button @click="copyWithdrawalAddress(withdrawalAddress.address)" class="p-2 text-[var(--accent-color)] hover:text-[var(--accent-color-50)] transition">
									<svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
										<path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2"/>
										<rect x="8" y="8" width="12" height="12" rx="2" ry="2"/>
									</svg>
								</button>
							</div>

							<button @click="showFullWithdrawalModal = false" class="mt-4 px-6 py-2 bg-[var(--accent-color)] border border-[var(--accent-color)] text-white rounded-full hover:bg-transparent hover:text-[var(--accent-color)] transition">
								Close
							</button>
						</div>
					</div>
				</div>
			</transition>


			<!-- DEPOSIT ZAR POPUP -->
			<transition class="fade-in">
				<DepositZarPopup
					v-if="showDepositPopup"
					:current-theme="currentTheme"
					:wallet-address="walletAddress"
					:evm-accounts="evmAccounts"
					:deposit-reference="depositReference"
					:withdrawal-address="withdrawalAddress"
					@close="showDepositPopup = false"
					@next="handleDepositNext"
					@add-account="handleAddAccount"
					@open-withdrawal-address="showWithdrawalAddressPopup = true"
				/>
			</transition>

			<transition class="fade-in">
				<!-- second “awaiting” popup -->
				<ConfirmDeposit
					v-if="showConfirmDeposit"
					:current-theme="currentTheme"
					:deposit-reference="depositReference"
					:withdrawal-address="withdrawalAddress"
					@back="showConfirmDeposit = false; showDepositPopup = true"
					@confirm="handleUserDeposit"
				/>
			</transition>

			<!--  KYB FORM POPOP Form Popup -->
			<transition class="fade-in">
				<KycForm 
					v-if="showKycForm" 
					:currentTheme="currentTheme"
					:loading="kycSubmitting" 
					@close="showKycForm = false"
					@submit-kyc="submitKyc" 
				/>
			</transition>

			<!-- Withdrawal Address Popup -->
			<transition name="fade-in">
				<WithdrawalAddressPopup
					v-if="showWithdrawalAddressPopup"
					:current-theme="currentTheme"
					:evm-accounts="evmAccounts"
					@close="showWithdrawalAddressPopup = false"
					:initial-selected="withdrawalAddress"
					@add-account="showAddAccountPopup = true"
					@confirm-withdrawal-address="handleWithdrawalAddressConfirm"
				/>
			</transition>

			<!--  Trade In DeFi POPUP -->
			<transition class="fade-in">
				<div v-if="showTradeConfirm" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" @click.self="showTradeConfirm = false" >
					<div class="popup-bg text-[var(--popup-text-color)] rounded-2xl p-6 w-11/12 md:w-2/3 lg:w-1/2 xl:w-2/5 animate-scale-in shadow-2xl text-center">
						<h2 class="text-2xl font-archivomedium text-[var(--text-color)] mb-2">
							Switch to Ethereum?
						</h2>
						<p class="text-sm text-[var(--secondary-text-color)] mb-6">
							You’ll be switched from Bitcoin → Ethereum and taken to WalletConnect. This may take a few seconds. 
						</p>
						<div class="flex justify-center space-x-4">
							<button @click="cancelTrade" class="px-4 py-2 rounded-full border border-[var(--accent-color)] text-[var(--text-color)] bg-transparent hover:bg-[var(--accent-color)] hover:text-white transition duration-200">
								Cancel
							</button>
							<button @click="confirmTrade" :disabled="switchLoading" class="flex items-center justify-center px-4 py-2 rounded-full border border-[var(--accent-color)] bg-[var(--accent-color)] hover:bg-transparent text-white hover:text-[var(--text-color)] transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed" >
								<div v-if="!switchLoading">
									Confirm
								</div>
								<div v-else class="loader w-4 h-4"></div>
							</button>
							<!--<button @click="confirmTrade" class="px-4 py-2 rounded-full border border-[var(--accent-color)] bg-[var(--accent-color)] hover:bg-transparent text-white hover:text-[var(--text-color)] transition duration-200">
								Confirm
							</button>-->
						</div>
					</div>
				</div>
			</transition>

			<!-- ── Deposit ZAR: Switch to Ethereum popup ── -->
			<transition class="fade-in">
				<div v-if="showDepositSwitchConfirm" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" @click.self="showDepositSwitchConfirm = false">
					<div class="popup-bg rounded-2xl p-6 w-11/12 md:w-2/3 lg:w-1/2 shadow-2xl text-center animate-scale-in">
						<h2 class="text-2xl font-archivomedium text-[var(--text-color)] mb-2">
							Switch to Ethereum?
						</h2>
						<p class="text-sm text-[var(--secondary-text-color)] mb-6">
							You’ll be switched from Bitcoin → Ethereum and shown the Deposit Popup. This may take a few seconds.
						</p>
						<div class="flex justify-center space-x-4">
							<button @click="cancelDepositSwitch" class="px-4 py-2 rounded-full border border-[var(--accent-color)] bg-transparent text-[var(--text-color)]
							hover:bg-[var(--accent-color)] hover:text-white transition">
								Cancel
							</button>
							<button @click="confirmDepositSwitch" :disabled="switchLoading" class="flex items-center justify-center px-4 py-2 rounded-full border border-[var(--accent-color)] bg-[var(--accent-color)] hover:bg-transparent text-white hover:text-[var(--text-color)] transition disabled:opacity-50 disabled:cursor-not-allowed">
								<div v-if="!switchLoading">
									Confirm
								</div>
								<div v-else class="loader w-4 h-4"></div>
							</button>

							<!--<button @click="confirmDepositSwitch" class="px-4 py-2 rounded-full border border-[var(--accent-color)] bg-[var(--accent-color)]
							hover:bg-transparent text-white hover:text-[var(--text-color)] transition">
								Confirm
							</button>-->
						</div>
					</div>
				</div>
			</transition>

			<!-- ── Switch to Ethereum for Withdrawal Address ── -->
			<transition class="fade-in">
				<div v-if="showWithdrawalSwitchConfirm" class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" @click.self="showWithdrawalSwitchConfirm = false">
					<div class="popup-bg rounded-2xl p-6 w-11/12 md:w-2/3 lg:w-1/2 shadow-2xl text-center animate-scale-in">
						<h2 class="text-2xl font-archivomedium text-[var(--text-color)] mb-2">
							Switch to Ethereum?
						</h2>
						<p class="text-sm text-[var(--secondary-text-color)] mb-6">
							You’ll be switched from Bitcoin → Ethereum and then shown the Withdrawal Address popup.
						</p>
						<div class="flex justify-center space-x-4">
							<button @click="cancelWithdrawalSwitch" class="px-4 py-2 rounded-full border border-[var(--accent-color)] text-[var(--text-color)] bg-transparent hover:bg-[var(--accent-color)] hover:text-white">
								Cancel
							</button>
							<button @click="confirmWithdrawalSwitch" :disabled="switchLoading" class="px-4 py-2 rounded-full border border-[var(--accent-color)] bg-[var(--accent-color)] hover:bg-transparent text-white hover:text-[var(--text-color)] flex items-center justify-center">
								<span v-if="!switchLoading">Confirm</span>
								<div v-else class="loader w-5 h-5"></div>
							</button>
						</div>
					</div>
				</div>
			</transition>


			<!-- Rejection Reason Modal -->
			<transition name="fade-in">
				<div v-if="showRejectionReason" class="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50">
					<div class="popup-bg rounded-lg p-6 w-11/12 md:w-1/3 shadow-2xl">
						<h2 class="text-2xl font-archivomedium text-[var(--text-color)] text-center my-4">
							Rejection Reason
						</h2>
						<p class="text-[var(--text-color)] mb-6">
							{{ rejectionReason }}
						</p>
						<button @click="showRejectionReason = false" class="px-4 py-2 flex mx-auto items-center justify-center rounded-full border border-[var(--accent-color)] bg-[var(--accent-color)] text-[var(--hover-text-color)] hover:bg-transparent hover:text-[var(--text-color)] transition">
							Close
						</button>
					</div>
				</div>
			</transition>
		</div>
	</div>
</template>

<script setup>
	import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue';
	import { Wallet as btcWallet } from 'cardware-btc';
	import { Wallet as evmWallet } from 'cardware-evm';
	//import { Wallet as evmWallet } from '../pkg';
	import QRCodeVue from 'qrcode.vue';
	import { QrcodeStream } from 'vue3-qrcode-reader';
	import { parseEther, parseUnits, Transaction, formatEther, ethers, formatUnits, isAddress, getAddress, JsonRpcProvider} from 'ethers';
	import SignClient from '@walletconnect/sign-client';
	import axios from 'axios';
	import { Buffer } from "buffer";

	axios.defaults.withCredentials = true;

	import LoginSignUpPage from './components/loginSignUpPage.vue';
	import PairingBegin from './components/pairingBegin.vue';
	import WalletOverview      from './components/walletOverview.vue';
	import PairingProcess from './components/pairingProcess.vue';
	import SidebarMenu from './components/sidebarMenu.vue';
	import SendAsset from './components/sendAsset.vue';
	import ReceiveAsset from './components/receiveAsset.vue';
	import ScanQRCodeSend from './components/scanQRCodeSend.vue';
	import DisplayPsbt from './components/displayPsbt.vue';
	import ScanSignedTransaction from './components/scanSignedTransaction.vue';
	import TradeView from './components/tradeScreen.vue';
	import WCScanner from './components/walletConnectScanner.vue';
	import ExportZpub from './components/exportZpub.vue';
	import TransactionCompleteScreen from './components/transactionComplete.vue';
	import AddTokenPopup from './components/addTokenContractPopup.vue';
	import ThemeSelectionPopup from './components/themeSelectionPopup.vue';
	import ResourcesPopup from './components/resourcesPopup.vue';
	import AddNetworkPopup from './components/addNetworkPopup.vue';
	import AddAccountPopup from './components/addAccountPopup.vue';
	import RemoveTokenAsset from './components/removeTokenAsset.vue';
	import AddressBookPopup from './components/addressBookPopup.vue';
	import TransactionHistory from './components/transactionHistory.vue';
	import KycForm from './components/kycForm.vue';
	import DepositZarPopup from './components/depositZarPopup.vue';
	import WithdrawalAddressPopup from './components/withdrawalAddressPopup.vue';
	import ResetPasswordPage from './components/resetPasswordPage.vue';
	import ConfirmDeposit from './components/confirmDeposit.vue';


	import lightLogo from './assets/CWWLogo.png';
	import darkLogo from './assets/LogoWhite1.png';
	import lightWallet from './assets/5Wallet.png';
	import darkWallet from './assets/WalletWhite1.png';
	import lightTransfers from './assets/TransIcon.png';
	import darkTransfers from './assets/TransfersWhite1.png';
	import lightMainSend from './assets/Send_LightTheme.png';
	import darkMainSend from './assets/Send_DarkTheme.png';
	import lightMainReceive from './assets/Receive_LightTheme.png';
	import darkMainReceive from './assets/Receive_DarkTheme.png';
	import lightSettings from './assets/SettIcon.png';
	import darkSettings from './assets/SettingsWhite.png';
	import lightResources from './assets/Resources.png';
	import darkResources from './assets/ResourcesWhite.png';
	import lightUnpair from './assets/Unpair.png';
	import darkUnpair from './assets/UnpairWhite1.png';
	import lightX from './assets/X.png';
	import darkX from './assets/XWhite.png';
	import lightDocs from './assets/Docs_Light_Theme.png';
	import darkDocs from './assets/Docs_Dark_Theme.png';
	import lightFAQs from './assets/FAQs_Light_Theme.png';
	import darkFAQs from './assets/FAQs_Dark_Theme.png';
	import lightEye from './assets/eye.png';
	import darkEye from './assets/eyeWhite.png';
	import lightSend from './assets/Send_Copper.png';
	import darkSend from './assets/SendIconOrange.png';
	import lightReceive from './assets/Receive_Copper.png';
	import darkReceive from './assets/ReceiveIconOrange.png';
	import lightCopy from './assets/LightCopyIcon.png';
	import darkCopy from './assets/DarkCopyIcon.png';
	import lightScan from './assets/QR_Light_Theme.png';
	import darkScan from './assets/QR_Dark_Theme.png';
	import darkOrangeSend from './assets/Dark_Orange_Send.png';
	import darkOrangeReceive from './assets/Dark_Orange_Receive.png';
	import lightGuide from './assets/Guide_Light_Theme.png';
	import darkGuide from './assets/Guide_Dark_Theme.png';
	import lightMultisig from './assets/Light_Theme_Multisig.png';
	import darkMultisig from './assets/Dark_Theme_Multisig.png';
	import lightExport from './assets/ExportLightTheme.png';
	import darkExport from './assets/ExportDarkTheme.png';
	import plusIcon from './assets/PlusLightTheme.png';
	import plusIconDarkTheme from './assets/PlusDarkTheme.png';
	import btcIcon from './assets/BTCLogo.png';
	import ethIcon from './assets/EthLogo1.png';
	import arbitrumIcon from './assets/ArbitrumLogo.png';
	import avalancheIcon from './assets/AvalancheLogo.png';
	import starknetIcon from './assets/StarknetLogo.png';
	import baseIcon from './assets/BaseLogo.png';
	import opIcon from './assets/OPLogo.png';
	import bnbIcon from './assets/BnbLogo.png';
	import polygonIcon from './assets/polygonLogo.png';
	import usdtIcon from './assets/USDTLogo3.png';
	import usdcIcon from './assets/USDCLogo1.png';
	import EtherscanLightTheme from './assets/Etherscan1.png';
	import EtherscanDarkTheme from './assets/EtherscanDarkTheme.png';
	import SonicscanLightTheme from './assets/SonicLogo.png';
	import SonicscanDarkTheme from './assets/SonicLogo.png';
	import placeholderIcon from './assets/InvalidIcon.png';
	import sonicIcon from './assets/SonicLogo.png';
	import PolygonscanLightTheme from './assets/polygonLogo.png';
	import PolygonscanDarkTheme from './assets/polygonLogo.png';
	import OptimisticEtherscanLightTheme from './assets/OPLogo.png';
	import OptimisticEtherscanDarkTheme from './assets/OPLogo.png';
	import BscscanLightTheme from './assets/BnbLogo.png';
	import BscscanDarkTheme from './assets/BnbLogo.png';
	import ArbiscanLightTheme from './assets/ArbitrumLogo.png';
	import ArbiscanDarkTheme from './assets/ArbitrumLogo.png';
	import SnowtraceLightTheme from './assets/AvalancheLogo.png';
	import SnowtraceDarkTheme from './assets/AvalancheLogo.png';
	import BasescanDarkTheme from './assets/BaseLogo.png';
	import BasescanLightTheme from './assets/BaseLogo.png';
	import walletConnectLogo1 from './assets/WalletConnectLogo1.png';
	import lightAccountIcon from './assets/lightAccountIcon.png';
	import darkAccountIcon from './assets/darkAccountIcon.png';

	import dapp1 from './assets/1inchLogo1.png'
	import dapp2 from './assets/crvDaoLogo1.png'
	import dapp3 from './assets/sushiswapLogo1.png'
	import dapp4 from './assets/uniswapLogo2.png'
	import dapp5 from './assets/pancakeswapLogo1.png'
	import dapp6 from './assets/pendleFinanceLogo1.png'
	import dapp7 from './assets/beefyFinanceLogo1.png'
	import dapp8 from './assets/StargateIcon.png'
	import dapp9 from './assets/aaveLogo1.png'
	import dapp10 from './assets/LidoIcon.png'
	import dapp11 from './assets/shadowSwap.png'

	const supportedDApps = [
		//{ name:'1inch', src: dapp1, url: 'https://app.1inch.io/' },
		{ name:'Curve Finance', src: dapp2, url: 'https://www.curve.finance/dex/' },
		{ name:'Uniswap', src: dapp4, url: 'https://app.uniswap.org/' },
		{ name:'Sushiswap', src: dapp3, url: 'https://www.sushi.com/ethereum/swap' },
		{ name:'Pancake Swap', src: dapp5, url: 'https://pancakeswap.finance/' },
		{ name:'Shadow So', src: dapp11, url: 'https://www.shadow.so/trade'},
		{ name:'Stargate Finance', src: dapp8, url: 'https://stargate.finance/bridge' },
		{ name:'Pendle Finance', src: dapp6, url: 'https://app.pendle.finance/trade/markets?utm_source=landing&utm_medium=landing' },
		{ name:'Aave', src: dapp9, url: 'https://app.aave.com/' },
		{ name:'Beefy', src: dapp7, url: 'https://app.beefy.com/' },
		{ name:'Lido', src: dapp10, url: 'https://stake.lido.fi/' },
	]

	const environment = process.env.VUE_APP_ENV;
	const baseURL = process.env.VUE_APP_API_BASE_URL;
	const otcURL = process.env.VUE_APP_API_OTC_URL;

	let walletPrefs = ref({
		id: 'v1.0.0',
		version: 1,
		versionOfDevice: ['BTC Only = 0', 'BTC & EVM = 1'],
		preferredChains: ['Bitcoin', 'Ethereum'],
		selectedChain: 'Bitcoin',
		selectedAccount: 'm/0/0',
		theme: 'light'
	});

	let walletTokens = ref({
		'Ethereum': {
		erc20: [
		{ address: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", decimals: 6, name: "USD Coin", symbol: "USDC" },
		{ address: "0xdAC17F958D2ee523a2206206994597C13D831ec7", decimals: 6, name: "Tether USD", symbol: "USDT" }
		]}
	});
	let currentTokens = ref(null);
	let tokenIcons = ref({
		'0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48': usdcIcon,
		'0xdAC17F958D2ee523a2206206994597C13D831ec7': usdtIcon
	});

	let walletAmounts = ref({
		native: 0,
		erc20: {}
	});

	let walletAccounts = ref({
		'Ethereum': [
		{ name: 'Account 1', address: '', derivationPath: 'm/0/0' }
		]
	});
	const selectedAccount = ref(null);

	let selectedChain = ref({
		name: 'Bitcoin',
		slug: 'bitcoin', 
		ticker: 'BTC',
		icon: btcIcon
	});
	const selectedAsset = ref('BTC');

	let wallet = null;
	let btcOnly = ref(true);

	const paired = ref(false);
	const pairingStarted = ref(false);
	let pairStrings = ["", "", ""];
	let pairPercent = 0;
	let pairScan = false;

	const derivationPath = ref('');
	const walletAddress = ref('');
	const balance = ref(0);
	const unconfirmedBalance = ref(0);
	const showUnconf = ref(false);
	const currentFee = ref('');
	const selectedFee = ref(-1);
	let maxBool = false;
	let maxAmount = 0;
	let chunks = [];
	let broadcastStrings = [];
	const sending = ref(false);
	const signed = ref(false);

	const isSyncing = ref(false);
	const isBalanceLoading = ref(false);
	const switchingChain = ref(false);

	let transactionDetails = ref({
		type: '',
		to: '',
		amount: '',
		amountUSD: '',
		fee: '',
		feeUSD: '',
		txHash: '',
		unsignedTx: ''
	});

	const txidStr = ref('');
	let qrCodeUrl = ref('');
	let debugInfo = ref('');
	const transactionSubmitted = ref(false);
	const transactionComplete = ref(false);

	let interval;
	const psbtGenerated = ref(false);
	const psbtText = ref('');
	const percentSynced = ref('0 %');
	const recipientAddress = ref('');
	const amountInput = ref('');
	const showReceive = ref(false);

	const unsignedTx = ref(""); 
	const chunkSize = 46;
	let qrIndex = 0;
	let currentIndex = 0;
	let firstTime = false;
	let totalBCodes = 0;
	let totalScanned = 0;
	let lastIndex = 0;
	let feeInSats;
	const decodedText = ref('');
	const isCameraActive = ref(false);

	const showBalance = ref(true);
	const isMobileMenuOpen = ref(false);
	const showThemePopup = ref(false);
	const showResourcesPopup = ref(false);
	const currentTheme = ref('light');
	const showCopyPopup = ref(false);
	const showComingSoonPopup = ref(false);

	const errorMessage = ref('');
	const successMessage = ref('');
	const infoMessage = ref('');
	const isScanningAddress = ref(false);
	const showAddNetworkPopup = ref(false);

	const chainDropdownOpen = ref(false);
	const chains = ref([
		{ name: 'Bitcoin', slug: 'bitcoin', ticker: 'BTC', icon: btcIcon, chainId: 0 },
		{ name: 'Ethereum', slug: 'ethereum', ticker: 'ETH', icon: ethIcon, chainId: 1 },
		{ name: 'Binance',  slug: 'binance-smart-chain', ticker: 'BNB', icon: bnbIcon, chainId: 56 },
		{ name: 'Polygon',  slug: 'polygon-pos', ticker: 'POL', icon: polygonIcon, chainId: 137 },
		{ name: 'Avalanche',slug: 'avalanche', ticker: 'AVAX', icon: avalancheIcon, chainId: 43114 },
		{ name: 'Sonic',   slug: 'sonic', ticker: 'S', icon: sonicIcon, chainId: 146 },
	]);

	const exportZpubView = ref(false);
	const zpub = ref('');

	const showTradeView = ref(false);

	const accounts = ref([{ name: 'Account 1', address: walletAddress.value }]);
	const accountDropdownOpen = ref(false);
	const showAddAccountPopup = ref(false);
	const newAccountName = ref('');
	const tokenContractDropdownOpen = ref(false);
	const showFullUnsignedTx = ref(false);
	const sendAccountDropdownOpen = ref(false);
	const assetDropdownOpen = ref(false);

	const isValidating = ref(false);
	const showAddTokenPopup = ref(false);
	const tokenPopupMode = ref('auto'); 
	const tokenAddress = ref('');
	const tokenDecimals = ref('');
	const tokenTicker = ref('');
	const tokenName = ref('');
	const assetMenuOpen = ref(null);
	const showRemoveConfirm = ref(false);
	const pendingRemoveAddr = ref(null);

	const priceError = ref('');
	const assetPrices = reactive({ native: 0, tokens: {} });

	const addressBook = ref([]);
	const showAddressBookPopup = ref(false);
	const newContactName     = ref('');
	const newContactAddress  = ref('');
	const addressMenuOpen = ref(false);

	const showPortfolioUSD = ref(false);
	const portfolioCalculating = ref(false);

	const addingContact = ref(addressBook.value.length === 0);

	const defaultTokens = [
		{ address: '0xb8c77482e45f1f44de1745f52c74426c631bdd52', symbol: 'BNB', name: 'Binance', decimals: 18 },
		{ address: '0xae7ab96520de3a18e5e111b5eaab095312d7fe84', symbol: 'STETH', name: 'Lido Staked ETH', decimals: 18 },
		{ address: '0x2260fac5e5542a773aa44fbcfedf7c193bc2c599', symbol: 'WBTC', name: 'Wrapped BTC', decimals: 8  },
		{ address: '0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2', symbol: 'WETH', name: 'Wrapped ETH', decimals: 18 },
		{ address: '0x6b175474e89094c44da98b954eedeac495271d0f', symbol: 'DAI', name: 'Dai Stablecoin', decimals: 18 },
		{ address: '0xd533a949740bb3306d119cc777fa900ba034cd52', symbol: 'CRV', name: 'Curve DAO Token', decimals: 18 },
		{ address: '0x4e3fbd56cd56c3e72c1403e103b45db9da5b9d2b', symbol: 'CVX', name: 'Convex Finance', decimals: 18 },
		{ address: '0x95ad61b0a150d79219dcf64e1e6cc01f0b64c4ce', symbol: 'SHIB', name: 'Shiba Inu', decimals: 18 },
		{ address: '0x6982508145454ce325ddbe47a25d4ec3d2311933', symbol: 'PEPE', name: 'Pepe Token', decimals: 18 },
		{ address: '0x7fc66500c84a76ad7e9c93437bfc5ac33e2ddae9', symbol: 'AAVE', name: 'AAVE Finance', decimals: 18 },
		{ address: '0x1f9840a85d5af5bf1d1762f925bdaddc4201f984', symbol: 'UNI', name: 'Uniswap', decimals: 18 },
		{ address: '0xfaba6f8e4a5e8ab82f62fe7c39859fa577269be3', symbol: 'ONDO', name: 'Ondo Finance', decimals: 18 },
		{ address: '0x808507121b80c02388fad14726482e061b8da827', symbol: 'PENDLE', name: 'Pendle Finance', decimals: 18 },
		{ address: '0xcf0c122c6b73ff809c693db761e7baebe62b6a2e', symbol: 'FLOKI', name: 'Floki', decimals: 18 },
	];

	const defaultTokensImported = ref(
		localStorage.getItem('cardware_default_tokens_imported') === 'true'
	);

	const isApprove = ref(false);
	const isSwap = ref(false);
	const isSign = ref(false);

	const wcMethods = [
		'eth_sendTransaction',
		'personal_sign',
		'eth_signTypedData_v4',
		'wallet_switchEthereumChain',
		'wallet_addEthereumChain'
	];
	const wcChains = [
		1, 56, 137, 146, 43114
	];
	const wcSwapMethods = new Set([
		//V2 DEXs (Uniswap, Sushi, Pancake)
		'0x7ff36ab5', // swapExactETHForTokens
		'0x18cbafe5', // swapExactTokensForETH
		'0x38ed1739', // swapExactTokensForTokens
		'0x8803dbee', // swapETHForExactTokens
		'0x4a25d94a', // swapTokensForExactETH
		'0x472b43f3', // swapTokensForExactTokens

		//V3 DEXs (Uniswap, Sushi, Pancake)
		'0x04e45aaf', // exactInput
		'0x5023b4df', // exactOutput
		'0xd33721a5', // exactInputSingle
		'0x09b81346', // exactOutputSingle

		//1inch
		'0x12aa3caf', // swap
		'0x2e95b6c8', // unoswap
		'0xe449022e', // unoswapTo
		'0x947f84fc', // fillOrderRFQ
	]);

	const showWCScanner = ref(false);
	const walletConnectUri = ref('');
	let wcSessions = ref({});
	let signClient = null;
	let walletConnectSign = false;
	let walletConnectMethod = "";
	let walletConnectTxHash = "";
	let walletConnectUnsignedTx = "";
	let walletConnectTopic;
	let walletConnectID;
	let wcfirstSig = false;
	let wcTxHash = ref('');

	const activeTab = ref('assets');
	const showHistory = ref(false);
	const allTransactions = ref([]);
	const loadingTransactions = ref(false);
	const highlightChain = ref(false);

	//enterprise
	const currentView = ref('account'); 
	const isLoggedIn = ref(false);
	const showTradeConfirm = ref(false);
	const showKycForm = ref(false);
	const userProfile = ref({ email: '', name: '', username: '', kycData: { companyName: '' } });
	const kycStatus = ref('Not Started');
	const rejectionReason = ref('');
	let kycInterval;
	const profileSaved   = ref(false);
	const loadingProfile = ref(false);
	const isEditingProfile = ref(false);
	const showDepositPopup = ref(false);
	const showConfirmDeposit = ref(false);
	const isProfileOpen = ref(false);
	const authLoading = ref(false);
	const kycSubmitting = ref(false);
	const showRejectionReason = ref(false);
	//const operations = ref([]);
	const depositReference = ref('');
	const withdrawalAddress = ref(null);
	const showWithdrawalAddressPopup = ref(false);
	const selectedWithdrawalAccount = ref(null);
	const showDepositSwitchConfirm = ref(false);
	const showFullWithdrawalModal = ref(false);

	const showWithdrawalSwitchConfirm = ref(false);
	const switchLoading = ref(false);

	const showResetPassword = ref(false);
	const resetToken = ref('');

	const statusEmoji = {
		'Not Started': '🚫',
		'Pending': '⏳',
		'Approved': '✅',
		'Rejected': '❌'
	};

	const userDeposits = ref([]);

	//================================================== COMPUTED  ==================================================

	const displayedBalance = computed(() => {
		return showPortfolioUSD.value ? portfolioChars.value : balance.value;
	})

	/*const operations = computed(() => {
		return userDeposits.value
			.map(dep => {
			const dt = new Date(dep.timeInitiated || dep.timestamp || dep.date);
			const iso = dt.toISOString();
			return {
				date: iso.split('T')[0],
				time: iso.split('T')[1].slice(0,8),
				type: 'Deposit ZAR',
				amount: dep.amount,
				withdrawalAddress: dep.withdrawalAddress,
				status: dep.status,
				ts: dt.getTime()
			};
		})
		.sort((a, b) => b.ts - a.ts);
	});*/

	/*const operations = computed(() => {
		return userDeposits.value
			.map(dep => {
			const raw = dep.createdAt || dep.timeInitiated || dep.timestamp || dep.date;
			const dt = new Date(raw);
			// if invalid, fall back to “now”
			const safeDt = isNaN(dt.getTime()) ? new Date() : dt;
			const iso = safeDt.toISOString();
			return {
				date: iso.split('T')[0],
				time: iso.split('T')[1].slice(0,8),
				type: dep.type || 'Deposit ZAR',
				amount: dep.amount,
				withdrawalAddress: dep.withdrawalAddress,
				status: dep.status,
				ts: safeDt.getTime()
			};
		})
		.sort((a, b) => b.ts - a.ts);
	});*/

	const operations = computed(() => {
		const deps = userDeposits.value || [];
			return deps
			.map(dep => {
			const raw = dep.createdAt || dep.timeInitiated || dep.timestamp || dep.date;
			const dt  = new Date(raw);
			const safeDt = isNaN(dt.getTime()) ? new Date() : dt;
			const iso = safeDt.toISOString();
			return {
			date: iso.split("T")[0],
			time: iso.split("T")[1].slice(0,8),
			type: dep.type || "Deposit ZAR",
			amount: dep.amount,
			withdrawalAddress: dep.withdrawalAddress,
			status: dep.status,
			ts: safeDt.getTime()
			};
		})
		.sort((a, b) => b.ts - a.ts);
	});

	const totalDeposits = computed(() => {
		return operations.value
		.filter(op => op.type.toLowerCase().includes('deposit'))
		.reduce((sum, op) => sum + parseFloat(op.amount || 0), 0)
		.toFixed(2);
	});

	const companyName = computed(() =>
		userProfile.value.kycData.companyName || '—'
	);

	const evmAccounts = computed(
		() => walletAccounts.value['Ethereum'] || []);

	const recentTransactions = computed(() => allTransactions.value.slice(0, 3));

	const assetList = computed(() => {
		if (selectedChain.value.name === 'Bitcoin') {
			const unit = assetPrices.native || 0;
			const bal  = Number(walletAmounts.value.native);
			const chainAssets = [{
				name: 'Bitcoin',
				symbol: 'BTC',
				icon: btcIcon,
				balance: walletAmounts.value.native,
				price: parseFloat((bal * unit).toFixed(2))
			}];
			return chainAssets;
		} else {
			const tempBalance = Number(walletAmounts.value.native);
			const chainAssets = [{
				name: selectedChain.value.name,
				symbol: selectedChain.value.ticker,
				icon: selectedChain.value.icon,
				balance: parseFloat(tempBalance.toFixed(8)),
				price: parseFloat((tempBalance * (assetPrices.native || 0)).toFixed(2))
			}];

			for (let i = 0; i < currentTokens.value.erc20.length; i++) {
				const token = currentTokens.value.erc20[i];
				const amount = Number(walletAmounts.value.erc20[token.address]) / Math.pow(10, token.decimals);
				const unit = assetPrices.tokens[token.address.toLowerCase()] || 0;

				chainAssets.push({
					name: token.name,
					symbol: token.symbol,
					icon: tokenIcons.value[token.address],
					address: token.address,
					balance: parseFloat(amount.toFixed(8)),
					decimals: token.decimals,
					price: parseFloat((amount * unit).toFixed(2))
				});
			}
			const nativeAsset = chainAssets[0];
			const tokenAssets = chainAssets.slice(1).sort((a, b) => b.price - a.price);
			return [ nativeAsset, ...tokenAssets ];
		}
	});

	const selectedAssetObject = computed(() => {
		return assetList.value.find(asset => asset.symbol === selectedAsset.value) || {};
	});

	const displayedChains = computed(() => {
		const alwaysShow = ['Bitcoin', 'Ethereum'];
		const preferred = walletPrefs.value.preferredChains && walletPrefs.value.preferredChains.length ? walletPrefs.value.preferredChains : alwaysShow;
		const filtered = chains.value.filter(c => alwaysShow.includes(c.name) || preferred.includes(c.name));
		const addNetworkIcon = (currentTheme.value === 'dark' || currentTheme.value === 'navy') ? plusIconDarkTheme : plusIcon;
		return [...filtered, { name: "Add Network", ticker: "", icon: addNetworkIcon }];
	});

	const availableNetworks = computed(() => {
		return chains.value.filter(c => !walletPrefs.value.preferredChains.includes(c.name));
	});

	const esploraUrl = computed(() => {
		switch (selectedChain.value.name) {
			case 'Bitcoin': return 'bitcoin';
			case 'Ethereum': return 'https://mainnet.infura.io/v3/498507efe2844f7cb9d8f25dd6e8f92b';
			case 'Sonic': return 'https://rpc.soniclabs.com';
			case 'Avalanche': return 'https://avalanche-mainnet.infura.io/v3/498507efe2844f7cb9d8f25dd6e8f92b';
			case 'Binance': return 'https://bsc-mainnet.infura.io/v3/498507efe2844f7cb9d8f25dd6e8f92b';
			case 'Polygon': return 'https://polygon-mainnet.infura.io/v3/498507efe2844f7cb9d8f25dd6e8f92b';
			default: return selectedChain.value.name.toLowerCase();
		}
	});

	const blockExplorerUrl = computed(() => {
		switch (selectedChain.value.name) {
			case 'Bitcoin': return 'https://blockstream.info';
			case 'Ethereum': return 'https://etherscan.io';
			case 'Sonic': return 'https://sonicscan.org';
			case 'Polygon': return 'https://polygonscan.com';
			case 'Binance': return 'https://bscscan.com';
			case 'Avalanche': return 'https://snowtrace.io';
			default: return 'https://etherscan.io';
		}
	});

	const balanceDigits = computed(() => {
		if (!showBalance.value) {
			return '••••••••••'.split('');
		}else{
			const amount = Number(Number(walletAmounts.value.native).toFixed(8));
			return amount.toString().split('');
		}
	});

	const shortAddress = computed(() => {
		if (walletAddress.value) {
			return walletAddress.value.slice(0, 4) + "...." + walletAddress.value.slice(-5);
		}
		return '';
	});

	const decodedTx = computed(() => {
		if (!unsignedTx.value || !unsignedTx.value.trim()) { return null; }
		let txHex = unsignedTx.value.trim();
		if (!txHex.startsWith("0x")) {
			txHex = "0x" + txHex;
		}
		try {
			return Transaction.from(txHex);
		} catch (err) {
			console.error("Failed to decode unsigned transaction:", err);
			return null;
		}
	});

	const unsignedTxHash = computed(() => {
		if (decodedTx.value) {
			if (decodedTx.value.hash) {
				return decodedTx.value.hash;
			} else if (decodedTx.value.unsignedHash) {
				return decodedTx.value.unsignedHash;
			}
		}
		return "";
	});

	const explorerIcon = computed(() => {
		switch(selectedChain.value.name) {
			case 'Sonic':
			return (currentTheme.value === 'dark' || currentTheme.value === 'navy') ? SonicscanDarkTheme: SonicscanLightTheme;
			case 'Polygon':
			return (currentTheme.value === 'dark' || currentTheme.value === 'navy') ? PolygonscanDarkTheme : PolygonscanLightTheme;
			case 'Binance':
			return (currentTheme.value === 'dark' || currentTheme.value === 'navy') ? BscscanDarkTheme : BscscanLightTheme;
			case 'Avalanche':
			return (currentTheme.value === 'dark' || currentTheme.value === 'navy') ? SnowtraceDarkTheme : SnowtraceLightTheme;
			default:
			return (currentTheme.value === 'dark' || currentTheme.value === 'navy') ? EtherscanDarkTheme : EtherscanLightTheme;
		}
	});

	const balanceLabel = computed(() =>
		showPortfolioUSD.value ? 'My Portfolio (USD)' : `My ${selectedChain.value.ticker} Balance`
	)

	const portfolioValue = computed(() => {
		const total = assetList.value.reduce((sum, a) => sum + (a.price || 0), 0)
		return total.toFixed(2)
	})

	const portfolioChars = computed(() => {
		if (!showBalance.value) {
			return '••••••••';
		}
		return `$${portfolioValue.value}`;
	})

	const availableUsd = computed(() => {
		const asset = selectedAssetObject.value;
		const unit = asset.symbol === selectedChain.value.ticker ? assetPrices.native : assetPrices.tokens[asset.address?.toLowerCase()] || 0;
		const usd = (asset.balance || 0) * (unit || 0);
		return usd.toFixed(2);
	})

	const priceDigits = computed(() => {
		const bal = parseFloat(balance.value);
		if (!showBalance.value || isNaN(bal) || assetPrices.native <= 0) {
			return '•••'.split('');
		}
		const val = (bal * assetPrices.native).toFixed(2);
			return `$${val}`.split('');
	});

	const amountUsd = computed(() => {
		const amt = parseFloat(amountInput.value);
		if (!amt || isNaN(amt)) return '';
		const unit = selectedAssetObject.value.symbol === selectedChain.value.ticker ? assetPrices.native : assetPrices.tokens[selectedAssetObject.value.address?.toLowerCase()] || 0;
		return (amt * unit).toFixed(2);
	});

	const feeUsdPrice = computed(() => {
		const feeNum = parseFloat(currentFee.value);
		if (!feeNum || isNaN(feeNum)) return '';
		return (feeNum * assetPrices.native).toFixed(2);
	});

	//================================================== INITIALIZATION ==================================================

	onMounted(async () => {
		const params = new URLSearchParams(window.location.search)
		const t = params.get('resetToken')
		if (t) {
			resetToken.value = t
			showResetPassword.value = true
		}

		try {
			await axios.get(`${otcURL}/api/otc/validate-token`,  {
				withCredentials: true
			});
			isLoggedIn.value = true;
		} catch (error) {
			await axios.post(`${otcURL}/api/otc/logout`, {}, {
				withCredentials: true
			});
			isLoggedIn.value = false;
		}
		if (isLoggedIn.value) {
			preInitialize();
			successMessage.value = `Welcome back, ${userProfile.value.name || userProfile.value.email}`;
			successMessage.value = 'Welcome back';
			setTimeout(() => (successMessage.value = ''), 3000);
		}
	});

	onBeforeUnmount(() => {
		clearInterval(kycInterval);
	});

	async function preInitialize() {
		switchingChain.value = true;
		isBalanceLoading.value = true;
		currentView.value = 'account';
		fetchProfile();
		fetchUserDeposits();
		initializePrefs();
		initializeAccounts();
		initializeTokens();
		addressBook.value = JSON.parse(localStorage.getItem('cardware_address_book') || '[]');
		const saved = JSON.parse(localStorage.getItem('cardware_token_icons') || '{}');
		Object.assign(tokenIcons.value, saved);
		const xpub = localStorage.getItem('cardware_xpub');
		if (xpub) {
			paired.value = true;
			await initializeWallet(xpub, selectedAccount.value.derivationPath);
			await getTxHistory();
		}
		await fetchPrices();
		kycInterval = setInterval(fetchProfile, 60000);
	}

	const initializePrefs = () => {
		const walletPrefsString = localStorage.getItem('cardware_prefs');
		if (walletPrefsString) {
			walletPrefs.value = JSON.parse(walletPrefsString);
			if (walletPrefs.value.theme) {
				currentTheme.value = walletPrefs.value.theme;
			}
			walletPrefs.value.selectedChain = 'Ethereum';
			if (walletPrefs.value.selectedChain) {
				selectedChain.value = getChain(walletPrefs.value.selectedChain);
				selectedAsset.value = selectedChain.value.ticker;
			} else {
				localStorage.setItem('cardware_prefs', JSON.stringify(walletPrefs.value));
			}
		} else {
			walletPrefs.value.selectedChain = 'Ethereum';
			localStorage.setItem('cardware_prefs', JSON.stringify(walletPrefs.value));
		}
		if (walletPrefs.value.version == 0) {
			btcOnly.value = true;
		} else {
			btcOnly.value = false;
		}
	}

	const initializeAccounts = () => {
		const walletAccountsString = localStorage.getItem("cardware_accounts");
		if (walletAccountsString) {
			walletAccounts.value = JSON.parse(walletAccountsString);
			if (!walletAccounts.value[selectedChain.value.name]) {
				walletAccounts.value[selectedChain.value.name] = [];
				const mainAccount = {
					name: "Account 1",
					address: "",
					derivationPath: "m/0/0"
				};
				walletAccounts.value[selectedChain.value.name].push(mainAccount);
				localStorage.setItem('cardware_accounts', JSON.stringify(walletAccounts.value));
			}
		} else {
			walletAccounts.value[selectedChain.value.name] = [];
			const mainAccount = {
				name: "Account 1",
				address: "",
				derivationPath: "m/0/0"
			};
			walletAccounts.value[selectedChain.value.name].push(mainAccount);
			localStorage.setItem('cardware_accounts', JSON.stringify(walletAccounts.value));
		}

		const accountsList = walletAccounts.value[selectedChain.value.name];
		let found = null;
		if (walletPrefs.value.selectedAccount) {
			for (const account of accountsList) {
				if (account.derivationPath === walletPrefs.value.selectedAccount) {
					found = account;
					break;
				}
			}
		}
		if (found) {
			selectedAccount.value = found;
		} else {
			selectedAccount.value = accountsList[0];
		}
	}

	const initializeTokens = () => {
		const walletTokensString = localStorage.getItem("cardware_tokens");
		if (walletTokensString) {
			walletTokens.value = JSON.parse(walletTokensString);
		} else {
			localStorage.setItem("cardware_tokens", JSON.stringify(walletTokens.value));
		}
		if (selectedChain.value.name !== "Bitcoin") {
			const chainName = selectedChain.value.name;
			if (!walletTokens.value[chainName]) {
				walletTokens.value[chainName] = { erc20: [] };
			}
			currentTokens.value = walletTokens.value[chainName];
			currentTokens.value.erc20.forEach(t => {
				const key = t.address.toLowerCase();
				t.icon = tokenIcons.value[key] || `${process.env.BASE_URL}token-icons/${selectedChain.value.slug}/${key}/logo.png`;
			});
			walletAmounts.value = { native: 0, erc20: {} };
			currentTokens.value.erc20.forEach(t => {
				walletAmounts.value.erc20[t.address] = 0;
			});
		} else {
			walletAmounts.value = { native: 0 };
			currentTokens.value = { erc20: [] };
		}
	}

	const initializeWallet = async (xpub, derivation) => {
		try {
			zpub.value = xpub;
			derivationPath.value = derivation;
			if (selectedChain.value.name === 'Bitcoin') {
				wallet = await new btcWallet(
					[xpub],
					"https://btc.cardwarewallet.com",
					"bitcoin"
				);
			} else {
				let chainId = BigInt(selectedChain.value.chainId);
				wallet = await new evmWallet(
					xpub,
					derivation,
					esploraUrl.value,
					chainId
				);
				let addr = wallet.address();
				const result = await wallet.sync();
				let rawChainId = wallet.get_chain_id();
				let chainIdStr  = rawChainId.toString();
				await initializeWalletConnect(addr, chainIdStr);
			}
			walletAddress.value = wallet.address();
			const parts = derivation.split('/');
			const accountIndex = Number(parts[1]);
			walletAccounts.value[selectedChain.value.name][accountIndex].address = walletAddress.value;
			localStorage.setItem('cardware_accounts', JSON.stringify(walletAccounts.value));
			selectedAccount.value = walletAccounts.value[selectedChain.value.name][accountIndex];
			await callToSync();
		} catch (error) {
			paired.value = false;
			showErrorToast("Failed to initialize wallet: " + error.message);
		}
	}

	const backToOverview = () => {
		broadcastStrings = [];
		recipientAddress.value = '';
		amountInput.value = '';
		currentFee.value = '';
		walletConnectUri.value = '';
		selectedFee.value = -1;
		totalScanned = 0;
		pairPercent = 0;
		percentSynced.value = pairPercent + " %";
		showReceive.value = false;
		sending.value = false;
		signed.value = false;
		maxBool = false;
		maxAmount = 0;
		transactionSubmitted.value = false;
		transactionComplete.value = false;
		chainDropdownOpen.value = false;
		exportZpubView.value = false;
		isMobileMenuOpen.value = false;
		tokenContractDropdownOpen.value = false;
		accountDropdownOpen.value = false;
		sendAccountDropdownOpen.value = false;
		assetMenuOpen.value = false;
		assetDropdownOpen.value = false;
		showTradeView.value = false;
		isApprove.value = false;
		isSwap.value = false;
		isSign.value = false;
		showHistory.value = false;
		addressMenuOpen.value = false;
		currentView.value = 'wallet';
	}

	const backToWallet = async () => {
		broadcastStrings = [];
		txidStr.value = '';
		recipientAddress.value = '';
		amountInput.value = '';
		currentFee.value = '';
		walletConnectUri.value = '';
		selectedFee.value = -1;
		totalScanned = 0;
		pairPercent = 0;
		maxBool = false;
		maxAmount = 0;
		percentSynced.value = pairPercent + " %";
		showReceive.value = false;
		sending.value = false;
		signed.value = false;
		transactionSubmitted.value = false;
		transactionComplete.value = false;
		chainDropdownOpen.value = false;
		exportZpubView.value = false;
		tokenContractDropdownOpen.value = false;
		accountDropdownOpen.value = false;
		sendAccountDropdownOpen.value = false;
		assetMenuOpen.value = false;
		assetDropdownOpen.value = false;
		showTradeView.value = false;
		isApprove.value = false;
		isSwap.value = false;
		isSign.value = false;
		currentView.value = 'wallet';
		await callToSync();
	}

	setInterval(() => {
		fetchPrices();
	}, 300000);

	//================================================== PAIRING ==================================================

	const startPairing = async () => {
		pairingStarted.value = true;
		pairStrings = ['', '', ''];
		pairPercent = 0;
		percentSynced.value = pairPercent + ' %';
		pairScan = true;
	}

	const continueToPair = async(qrString) => {
		if(qrString.length > 0){
			if (qrString.charAt(0) == '1') {
				let parts = qrString.split(",");
				if (pairStrings[0] == '') {
					pairStrings[0] = parts[1];
					pairPercent += 33;
					percentSynced.value = pairPercent + ' %';
				}
			} else if(qrString.charAt(0) == '2') {
				let parts = qrString.split(",");
				if (pairStrings[1] == '') {
					pairStrings[1] = parts[1];
					pairPercent += 33;
					percentSynced.value = pairPercent + ' %';
				}
			} else if(qrString.charAt(0) == '3') {
				let parts = qrString.split(",");
				if (pairStrings[2] == '') {
					pairStrings[2] = parts[1];
					pairPercent += 33;
					percentSynced.value = pairPercent + ' %';
				}
			}
		}
		if (pairPercent == 99 && !paired.value) {
			try {
				let fullString = pairStrings[0] + pairStrings[1] + pairStrings[2];
				let parts = fullString.split(":");
				const xpub = parts[0];
				if (parts.length > 2) {
					walletPrefs.value.version = parts[2];
					if (parts[2] == '0') {
						btcOnly.value = true;
					} else {
						btcOnly.value = false;
					}
				} else {
					walletPrefs.value.version = 0;
					btcOnly.value = true;
				}
				paired.value = true;
				await initializeWallet(xpub, "m/0/0");
				localStorage.setItem('cardware_prefs', JSON.stringify(walletPrefs.value));
				localStorage.setItem('cardware_xpub', xpub);
			} catch (error) {
				pairingStarted.value = false;
			}
		}
	}

	const backToPair = () => {
		pairingStarted.value = false;
	}

	async function unpairWallet() {
		try {
			await axios.post(`${otcURL}/api/otc/logout`, {}, {
				withCredentials: true
			});
		} catch (err) {
			console.warn('Logout endpoint failed, but continuing to unpair.');
		}
		localStorage.removeItem("cardware_xpub");
		localStorage.removeItem('cardware_trusted_pending');
		localStorage.removeItem('cardware_wc_sessions');
		localStorage.removeItem('cardware_prefs');
		isLoggedIn.value = false;
		paired.value = false;
		window.location.reload();
	}

	//================================================== SYNC ==================================================

	const satoshisToBTC = (satoshis) => {
		const satoshisStr = satoshis.toString();
		const length = satoshisStr.length;
		if (length <= 8) {
			const padded = satoshisStr.padStart(9, '0');
			return `0.${padded.slice(-8)}`;
		} else {
			const integerPart = satoshisStr.slice(0, length - 8);
			const decimalPart = satoshisStr.slice(-8);
			return `${integerPart}.${decimalPart}`;
		}
	}

	const callToSync = async () => {
		if (isSyncing.value) { return; }
		isSyncing.value = true;
		if (selectedChain.value.name == 'Bitcoin') {
			checkTrustedPending();
		}
		balance.value = "";
		const result = await wallet.sync();
		if (!result.includes("Error:")) {
			if (selectedChain.value.name == 'Bitcoin') {
				const balanceSatoshis = wallet.balance();
				balance.value = satoshisToBTC(balanceSatoshis);
				walletAmounts.value.native = satoshisToBTC(balanceSatoshis);
				const unconfBalanceSatoshis = wallet.unconfirmed_balance();
				unconfirmedBalance.value = satoshisToBTC(unconfBalanceSatoshis);
				if (unconfBalanceSatoshis != 0) {
					showUnconf.value = true;
				} else {
					showUnconf.value = false;
				}
				saveTrustedPending();
			} else {
				balance.value = wallet.balance();
				walletAmounts.value.native = wallet.balance();
				let tokenAddresses = [];
				for (let i = 0; i < currentTokens.value.erc20.length; i++) {
					tokenAddresses.push(currentTokens.value.erc20[i].address);
				}
				let balances = await wallet.erc20_balance(tokenAddresses);
				if (balances[0].includes("Error:")) {
					balances = await wallet.erc20_balance(tokenAddresses);
				}
				for (let i = 1; i < balances.length; i++) {
					walletAmounts.value.erc20[tokenAddresses[i-1]] = balances[i];
				}
				/*for (let i = 0; i < tokenAddresses.length; i += 8) {
				const balances = await wallet.erc20_balance(tokenAddresses.slice(i, i + 8));
				console.log(balances);
				let index = i-1+(balances.length*i-1);
				for (let i = 1; i < balances.length; i++) {
				walletAmounts.value.erc20[tokenAddresses[index]] = balances[i];
				}
				delay(5000);
				}*/
				showUnconf.value = false;
			}
			await getTxHistory();
		} else if (result == "Error: Invalid extended public key.") {
			unpairWallet();
		} else {
			showErrorToast(result.replace(/^Error: /, ""));
		}
		isSyncing.value = false;
		switchingChain.value = false;
		isBalanceLoading.value = false;
	}

	const startSync = async () => {
		try {
			if (!isSyncing.value) {
				await callToSync();
			}
		} catch (error) {
			console.error('Sync failed:', error);
			showErrorToast("Failed to sync the wallet. Please try again.");   
		}
	}

	const checkTrustedPending = () => {
		const trustedPending = localStorage.getItem('cardware_trusted_pending');
		if(trustedPending){
			const trustedPendingArray = JSON.parse(trustedPending);
			wallet.set_trusted_pending(trustedPendingArray);
		}
	}

	const saveTrustedPending = () => {
		const trustedPending = wallet.get_trusted_pending();
		if (trustedPending.length > 0) {
			const trustedPendingString = JSON.stringify(trustedPending);
			localStorage.setItem('cardware_trusted_pending', trustedPendingString);
		} else{
			localStorage.setItem('cardware_trusted_pending', "");
		}
	}

	//=============================================== TX HISTORY ==================================================

	const getTxHistory = async () => {
		if (!wallet) {
			allTransactions.value = [];
			return;
		}

		let raw = '';
		try {
			if (selectedChain.value.name === 'Bitcoin') {
				raw = await wallet.get_tx_history(derivationPath.value || 'm/0/0');
			} else {
				raw = await wallet.get_tx_history('https://api.etherscan.io/v2/api', '9JCW66ACJA364YPEBCV4SFZDFEAR3DYSAK', 10);
			}
		} catch (err) {
			console.error('get_tx_history error:', err);
			//allTransactions.value = [];
			return;
		}

		if (typeof raw === 'string' && raw.startsWith('Error:')) {
			//allTransactions.value = [];
			return;
		}

		let parsed;
		try {
			parsed = JSON.parse(raw);
		} catch {
			showErrorToast('Failed to parse transaction history');
			return;
		}

		let btcTip = 0;
		let evmTip = 0;
		if (selectedChain.value.name === 'Bitcoin') {
			try {
				const res = await axios.get(`https://btc.cardwarewallet.com/blocks/tip/height`);
				btcTip = parseInt(res.data, 10) || 0;
			} catch (e) {
				console.error('BTC tip height error', e);
			}
		} else {
			try {
				const provider = new JsonRpcProvider(esploraUrl.value);
				evmTip = await provider.getBlockNumber();
			} catch (e) {
				console.error('EVM tip height error', e);
			}
		}

		const mapped = parsed.map(tx => {
			if (selectedChain.value.name === 'Bitcoin') {
				const block = tx.status?.block_height ?? 0;
				const confirmations = block ? btcTip - block + 1 : 0;
				const date  = tx.status?.block_time ? new Date(tx.status.block_time * 1000).toLocaleString() : 'Pending';
				//const date = tx.status.block_time ? new Date(tx.status.block_time * 1000).toLocaleString() : 'Pending';
				const type = tx.tx_type[0].toUpperCase() + tx.tx_type.slice(1);

				return {
					hash: tx.txid,
					date,
					block,
					inputsArr: tx.vin.map(v => `${v.txid}:${v.vout}`),
					outputsArr: tx.vout.map(o => o.scriptpubkey_address),
					outputValuesArr: tx.vout.map(o => (o.value / 1e8).toFixed(8) + ' BTC'),
					amount: `${(Math.abs(tx.amount) / 1e8).toFixed(8)} BTC`,
					fees: `${(tx.fee / 1e8).toFixed(8)} BTC`,
					confirmations,
					type,
					status: tx.status.confirmed ? 'Confirmed' : 'Pending',
				};
			} else {
				const confirmations = tx.block_number ? evmTip - tx.block_number + 1 : 0;
				const ts = tx.timeStamp || tx.timestamp;
				const date = ts
				? new Date(Number(ts) * 1000).toLocaleString()
				: 'Pending';

				const newAmount = Number(tx.value_eth).toFixed(8).replace(/\.?0+$/, '');
				return {
					block: tx.block_number,
					date,
					type: tx.direction[0].toUpperCase() + tx.direction.slice(1),
					amount: `${newAmount} ${selectedChain.value.ticker}`,
					from: tx.from_address,
					to: tx.to_address,
					confirmations,
					wei: `${formatEther(BigInt(tx.value_wei))} ${selectedChain.value.ticker}`,
					hash: tx.hash,
				};
			}
		});

		allTransactions.value = mapped;
	}

	const copyHash = async (hash) => {
		try {
			await navigator.clipboard.writeText(hash);
			showSuccessToast('Tx Hash copied to clipboard');
		} catch {
			showErrorToast('Failed to copy hash');
		}
	}

	const handleCopyAddress = async(address)=> {
		try {
			await navigator.clipboard.writeText(address);
			showSuccessToast('Address copied to clipboard');
		} catch {
			showErrorToast('Failed to copy address');
		}
	}
	//========================================== WALLET CONNECT ============================================

	const openTradeView = () => {
		showTradeView.value = true;
		psbtGenerated.value = false;
		showReceive.value = false;
		sending.value = false;
		showHistory.value = false;
		exportZpubView.value = false;
		showHistory.value = false;
	}

	const startWalletConnectQRAddress = () => {
		showWCScanner.value = true;
	}

	const stopWCScanner = () => {
		showWCScanner.value = false
	}

	const onWCDecode = async (result) => {
		if (result && result.startsWith('wc:')) {
			walletConnectUri.value = result;
			setTimeout(() => { showWCScanner.value = false }, 500);
			await walletConnect();         
		} else {
			showErrorToast('Invalid WalletConnect QR code');
			setTimeout(() => { showWCScanner.value = false }, 3000);
		}
	}

	const hexToBytes = (hex) => {
		if (hex.startsWith('0x')) { hex = hex.slice(2) }
		const bytes = []
		for (let i = 0; i < hex.length; i += 2) {
			bytes.push(parseInt(hex.slice(i, i + 2), 16))
		}
		return new Uint8Array(bytes)
	}

	const bytesToBase64 = (bytes) => {
		let str = '';
		for (let b of bytes) { str += String.fromCharCode(b) }
		return btoa(str);
	}

	const base64ToHex = (base64) => {
		const binaryStr = atob(base64);
		let hex = '0x';
		for (let i = 0; i < binaryStr.length - 4; i++) {
			const hexByte = binaryStr.charCodeAt(i).toString(16).padStart(2, '0');
			hex += hexByte;
		}
		return hex;
	}

	const initializeWalletConnect = async (address, chainId) => {
		if (signClient) {
			if (switchingChain.value) {
				//await updateWalletConnect(address, chainId);
			}
			return;
		}

		signClient = await SignClient.init({
			projectId: '4e828e2640dd9c8983a6d84b315cdc33',
			relayUrl: 'wss://relay.walletconnect.com'
		});

		const wcSessionsString = localStorage.getItem('cardware_wc_sessions');
		if (wcSessionsString) {
			wcSessions.value = JSON.parse(wcSessionsString);
		} else {
			wcSessions.value = {};
		}

		try {
			signClient.on('session_proposal', async (proposal) => {
				const { id, params: { requiredNamespaces } } = proposal;

				const checksumAddress = getAddress(address);
				let account = `eip155:${chainId}:${checksumAddress}`;

				const { acknowledged, topic } = await signClient.approve({
					id,
					namespaces: {
						eip155: {
							accounts: [account],
							methods: wcMethods,
							events: ['chainChanged', 'accountsChanged', 'disconnect'],
						},
					},
				});

				const session = signClient.session.get(topic);
				console.log("WALLET CONNECT SESSION: ", session);

				const sessionData = {
					name: session.peer.metadata.name,
					icon: session.peer.metadata.icons[0],
					url: session.peer.metadata.url,
					expiry: session.expiry
				}
				wcSessions.value[topic] = sessionData;
				localStorage.setItem('cardware_wc_sessions', JSON.stringify(wcSessions.value));

				console.log("OUR SESSIONS: ", wcSessions.value);
			});
		} catch {
			console.error("WC PROPOSAL ERROR: ", error);
		}
		
		try {
			signClient.on('session_request', async ({ topic, params, id }) => {
				const { request } = params;
				console.log("WALLET CONNECT METHOD");
				console.log(request.method);

				if (!wcMethods.includes(request.method)) {
					await signClient.respond({
						topic,
						response: {
							id,
							jsonrpc: "2.0",
							error: { code: 4200, message: "Method not supported" }
						}
					});
					return;
				}

				walletConnectMethod = request.method;
				let wcBlob;

				if (request.method === 'wallet_switchEthereumChain') {
					console.log("WALLET CONNECT SWITCH CHAIN");
					console.log(request);
					const newChainId = parseInt(request.params[0].chainId, 16);
					if (wcChains.includes(newChainId)) {
						if (newChainId !== selectedChain.value.chainId) {
							const newChain = chains.value.find(c => c.chainId === newChainId);
							await selectChain(newChain);
						} else {
							await updateWalletConnect(selectedAccount.value.address, newChainId);
						}

						await signClient.respond({
							topic,
							response: { id, jsonrpc: "2.0", result: null }
						});
					} else {
						await signClient.respond({
							topic,
							response: {
								id,
								jsonrpc: "2.0",
								error: { code: 4902, message: "Chain not added in wallet" }
							}
						});
					}
					return;
				}

				if (request.method === 'eth_sendTransaction') {
					const wcData = request.params[0];
					if(!wcData.to || !wcData.data){
						showErrorToast('Missing contract address and/or data');
						return; 
					}

					const methodID = wcData.data.slice(0, 10);
					isApprove.value = methodID === '0x095ea7b3';
					isSwap.value = wcSwapMethods.has(methodID);
					isSign.value = false;

					let tempValue = "";
					if (wcData.value) { tempValue = wcData.value; }
					let tempMaxPFee = "";
					if (wcData.maxPriorityFeePerGas) { tempMaxPFee = wcData.maxPriorityFeePerGas; }
					let tempMaxPerGas = "";
					if (wcData.maxFeePerGas) { tempMaxPerGas = wcData.maxFeePerGas; }
					let tempGas = "";
					if (wcData.gas){ tempGas = wcData.gas; }
					wcBlob = await wallet.prepare_eip1559(wcData.to, tempValue, tempMaxPFee, tempMaxPerGas, tempGas, wcData.data);
				}

				if (request.method === 'eth_signTypedData_v4' || request.method === 'personal_sign') {
					const wcAddress = request.params[0];
					const wcData = request.params[1];
					isApprove.value = false;
					isSwap.value = false;
					isSign.value = true;
					if (request.method === 'eth_signTypedData_v4') {
						wcBlob = await wallet.prepare_sign_typed_data_v4(wcData);
					}
					if (request.method === 'personal_sign') {
						wcBlob = await wallet.prepare_personal_sign(wcData);
					}
				}

				const [rawUnsigned, hashToSign] = wcBlob.split(":&");
				walletConnectSign = true;
				walletConnectUnsignedTx = '02' + rawUnsigned;
				walletConnectTxHash = hashToSign;
				walletConnectTopic = topic;
				walletConnectID = id;
				walletConnectSend();
			});
		} catch {
			console.error("WC REQUEST ERROR: ", error);
		}
		
		try {
			signClient.on('session_delete', ({ topic }) => {
				if (wcSessions.value[topic]) {
					const dAppName = wcSessions.value[topic].name;
					delete wcSessions.value[topic];
					localStorage.setItem('cardware_wc_sessions', JSON.stringify(wcSessions.value));
					showSuccessToast(`You have disconnected from ${dAppName}`);
				}
			});
		} catch (error) {
			console.error("WC DELETE ERROR: ", error);
		}
	}

	const connectWalletConnect = async () => {
		if (!walletConnectUri.value) {
			showErrorToast('Please paste a WalletConnect URI or scan the QR Code');
			return;
		}
		try {
			await signClient.core.pairing.pair({ uri: walletConnectUri.value });
			walletConnectUri.value = '';
			showSuccessToast('Successfully connected. Visit your dApp to trade');
			highlightChain.value = true;
			setTimeout(() => { highlightChain.value = false }, 3000);
		} catch (e) {
			showErrorToast('Failed to pair to dApp with WalletConnect');
		}
	}

	const updateWalletConnect = async (address, chainId) => {
		const checksumAddress = getAddress(address);
		let account = `eip155:${chainId}:${checksumAddress}`;

		for (const session of signClient.session.getAll()) {
			try {
				const currentChain = session.namespaces.eip155.accounts[0];
				if (currentChain === accountString) continue; 
				const ns = structuredClone(session.namespaces);
				const eip = ns.eip155;

				if (!eip.accounts.includes(account)) {
					eip.accounts.unshift(account); 
				} else {
					const i = eip.accounts.indexOf(account);
					if (i > 0) {
						eip.accounts.splice(i, 1);
						eip.accounts.unshift(account);
					}
				}
				ns.eip155 = { ...eip };

				await signClient.update({
					topic: session.topic,
					namespaces: ns
				});

				await signClient.emit({
					topic: session.topic,
					event: {
						name: "chainChanged",
						data: `0x${Number(chainId).toString(16)}`,
					},
					chainId: `eip155:${chainId}`
				});

				await signClient.emit({
					topic: session.topic,
					event: { name: 'accountsChanged', data: [checksumAddress] },
					chainId: `eip155:${chainId}`
				});
			} catch (error) {
				console.error("WC UPDATE SESSION ERROR: ", error);
			}
		}
	}

	const disconnectAllWalletConnect = async () => {
		const sessions = signClient.session.getAll();
		if (sessions.length === 0) {
			return showErrorToast('No active sessions to disconnect');
		}

		for (const session of sessions) {
			try {
				await signClient.disconnect({
					topic: session.topic,
					reason: { code: 6000, message: 'User requested shutdown' }
				});
			} catch (error) {
				if (!/session topic does not exist/.test(error.message)) {
					console.error("WC DELETE ALL SESSIONS ERROR: ", error);
				}
			}
		}

		wcSessions.value = {};
		localStorage.setItem('cardware_wc_sessions', JSON.stringify(wcSessions.value));
		showSuccessToast('You have successfully disconnected from all dApps');
	}

	const disconnectWalletConnect = async (topic) => {
		if (!signClient.session.get(topic)) {
			delete wcSessions.value[topic];
			localStorage.setItem('cardware_wc_sessions', JSON.stringify(wcSessions.value));
			showErrorToast('Session was already disconnected');
			return;
		}
		try {
			const dAppName = wcSessions.value[topic].name;
			await signClient.disconnect({
				topic,
				reason: { code: 6000, message: 'User requested shutdown' }
			});
			delete wcSessions.value[topic];
			localStorage.setItem('cardware_wc_sessions', JSON.stringify(wcSessions.value));
			showSuccessToast(`You have successfully disconnected from ${dAppName}`);
		} catch (error) {
			console.error("WC DELETE ERROR: ", error);
			showErrorToast('Failed to disconnect cleanly. Please try again.');
		}
	}

	//================================================== RECEIVE ==================================================

	const showReceiveView = () => {
		currentView.value = 'receive';
		sending.value = false;
		showTradeView.value = false;
		showHistory.value = false;
		exportZpubView.value = false;
		showWCScanner.value = false;
		showReceive.value = true;
		chainDropdownOpen.value = false;
	}

	const copyWalletAddress = async () => {
		try {
			await navigator.clipboard.writeText(walletAddress.value);
			showSuccessToast('Wallet address copied to clipboard');
		} catch (err) {
			showErrorToast('Failed to copy wallet address: ' + err);
		}
	}

	const startScanAddress = () => {
		isScanningAddress.value = true;
	}

	const stopScanAddress = () => {
		isScanningAddress.value = false;
	}

	//================================================== FEES ==================================================

	const setMaxAmount = () => {
		if (selectedChain.value.name === 'Bitcoin') {
			if (balance.value !== 0) {
				let blocks = 1;
				if (selectedFee.value !== -1) {
					switch (selectedFee.value) {
						case 0: blocks = 144; break;
						case 1: blocks = 18; break;
						case 2: blocks = 1; break;
					}
				} else {
					selectedFee.value = 2;
					blocks = 1;
				}
				let maxFee = wallet.estimate_sweep_fee(blocks);
				feeInSats = maxFee;
				let feeNum = (Number(maxFee)/100000000).toFixed(8);
				if (balance.value - feeNum > 0) {
					currentFee.value = feeNum + " BTC";
					amountInput.value = (balance.value - feeNum).toFixed(8);
					maxAmount = amountInput.value;
					maxBool = true;
				}
			}
		} else {
			if (selectedFee.value === -1) {
				selectedFee.value = 2;
			}
			const isNative = selectedAssetObject.value.symbol === selectedChain.value.ticker;
			const gasLimit = isNative ? 21000 : 160000;
			const rawFeeEth = wallet.estimate_fee(selectedFee.value, gasLimit);
			const feeWei = parseUnits(rawFeeEth.toString(), 18);
			const balWei = parseUnits(walletAmounts.value.native.toString(), 18);
			const feeNum = parseFloat(rawFeeEth);
			currentFee.value = `${feeNum.toFixed(8)} ${selectedChain.value.ticker}`;

			if (isNative) {
				if (balWei <= feeWei) {
					showErrorToast("Insufficient funds");
					maxBool = false;
				} else {
					const sendWei = balWei - feeWei;
					const sendEth = formatUnits(sendWei, 18);
					amountInput.value = sendEth;
					maxAmount = sendEth;
					maxBool = true;
				}
			} else {
				amountInput.value = selectedAssetObject.value.balance.toString();
				maxAmount = amountInput.value;
				maxBool = true;
				if (balWei < feeWei) {
					showErrorToast("Insufficient funds for gas");
					currentFee.value = "";
					maxBool = false;
				}
			}
		} 
	} 

	const adjustFeeRate = (newRate) => {
		selectedFee.value = newRate;
		if (maxBool && amountInput.value === maxAmount) {
			setMaxAmount();
			return;
		} else {
			maxBool = false;
		}
		if (!recipientAddress.value || !amountInput.value) {
			return;
		}
		if (selectedFee.value == -1) {
			return;
		}
		let blocks = 1;
		switch(newRate){
			case 0: blocks = 144; break;
			case 1: blocks = 18; break;
			case 2: blocks = 1; break;
		}
		if (selectedChain.value.name == 'Bitcoin') {
			let amountInSats = BigInt(Math.floor(amountInput.value*100000000));
			let amounts = [amountInSats];
			let recipients = [recipientAddress.value];
			let fee = wallet.estimate_fee(recipients, amounts, blocks);
			if (fee > 5 ) {
				let feeString = (Number(fee)/100000000).toFixed(8);
				currentFee.value = feeString + " BTC";
				feeInSats = BigInt(fee);
			} else {
				const feeInt = Number(fee);
				switch (feeInt) {
					case 0: errorMessage.value = "The addresses array and the amounts array are not the same length"; break;
					case 1: errorMessage.value = "There is an issue parsing the network"; break;
					case 2: errorMessage.value = "There is an invalid recipient address"; break;
					case 3: errorMessage.value = "There is an issue fetching the fee estimates"; break;
					case 4: errorMessage.value = "There is insufficient BTC to make this transaction"; break;
					case 5: errorMessage.value = "There are no UTXOs to spend"; break;
				}
				showErrorToast(errorMessage.value);
			}
		} else {
			const isNative = selectedAssetObject.value.symbol === selectedChain.value.ticker;
			const gasLimit = isNative ? 21000 : 160000;
			const rawFeeEth = wallet.estimate_fee(selectedFee.value, gasLimit);
			const feeWei = parseUnits(rawFeeEth.toString(), 18);
			currentFee.value = `${parseFloat(rawFeeEth).toFixed(8)} ${selectedChain.value.ticker}`;

			const balWei = parseUnits(walletAmounts.value.native.toString(), 18);
			const amtWei = isNative ? parseUnits(amountInput.value.toString(), 18) : parseUnits(amountInput.value.toString(), selectedAssetObject.value.decimals);

			const needs = isNative ? feeWei + amtWei : feeWei;
			if (balWei < needs) {
				showErrorToast("Insufficient funds");
			}
		}
	}

	const handleFirstFee = () => {
		if (!recipientAddress.value || !amountInput.value) {
			return;
		}
		adjustFeeRate(selectedFee.value);
	}

	//================================================== SEND ==================================================
	
	const startSend = () => {
		currentView.value = 'send';
		showReceive.value = false;
		showTradeView.value = false;
		showHistory.value = false;
		exportZpubView.value = false;
		showWCScanner.value = false;
		sending.value = true;
		psbtGenerated.value = false;
		chainDropdownOpen.value = false;
		sendAccountDropdownOpen.value = false;
		showReceiveView.value = false;
		recipientAddress.value = '';
		amountInput.value = '';
		currentFee.value = '';
		maxBool = false;
	}

	const decodeERC20Transfer = (inputData) => {
		let hex = inputData.slice(2);
		hex = hex.slice(8);
		const toHexPadded = hex.slice(0, 64);
		transactionDetails.value.to = "0x" + toHexPadded.slice(24);
		const amountHex = hex.slice(64);
		const amountRaw = BigInt("0x" + amountHex);
		const amountTokens = formatUnits(amountRaw.toString(), selectedAssetObject.value.decimals).toString();
		transactionDetails.value.amount = `${parseFloat(amountTokens)}`;
	}	

	const decodeUnsignedTx = (myUnsignedTx) => {
		if (!myUnsignedTx || !myUnsignedTx.trim()) { return; }
		let txHex = myUnsignedTx.trim();
		if (!txHex.startsWith("0x")) {
			txHex = "0x" + txHex;
		}
		try {
			const decodedTx = Transaction.from(txHex);
			//Legacy Transaction
			if (decodedTx.type == 0) {
				transactionDetails.value.type = decodedTx.typeName;
				transactionDetails.value.to = decodedTx.to;
				let tempAmount = formatUnits(decodedTx.value, 18);
				transactionDetails.value.amount = `${parseFloat(tempAmount)}`;
				transactionDetails.value.amountUSD = `$${(tempAmount * assetPrices.native).toFixed(2)}`;
				if (tempAmount === '0.0') {
					decodeERC20Transfer(decodedTx.data);
				}
				const tempFee = formatUnits(decodedTx.gasPrice * decodedTx.gasLimit, 18);
				transactionDetails.value.fee = `${parseFloat(tempFee).toFixed(8)} ${selectedChain.value.ticker}`
				transactionDetails.value.feeUSD = `$${(tempFee * assetPrices.native).toFixed(2)}`;
			}
			//EIP-1559 Transaction
			if (decodedTx.type == 2) {
				transactionDetails.value.type = decodedTx.typeName;
				transactionDetails.value.to = decodedTx.to;
				let tempAmount = formatUnits(decodedTx.value, 18);
				transactionDetails.value.amount = `${parseFloat(tempAmount)}`;
				transactionDetails.value.amountUSD = `$${(tempAmount * assetPrices.native).toFixed(2)}`;
				const tempFee = formatUnits(decodedTx.maxFeePerGas * decodedTx.gasLimit, 18);
				transactionDetails.value.fee = `${parseFloat(tempFee).toFixed(8)} ${selectedChain.value.ticker}`
				transactionDetails.value.feeUSD = `$${(tempFee * assetPrices.native).toFixed(2)}`;
			}
			if (decodedTx.gasPrice) {
				const tempFee = formatUnits(decodedTx.gasPrice * decodedTx.gasLimit, 18);
				transactionDetails.value.fee = `${parseFloat(tempFee).toFixed(8)} ${selectedChain.value.ticker}`
				transactionDetails.value.feeUSD = `$${(tempFee * assetPrices.native).toFixed(2)}`;
			} else {
				const tempFee = formatUnits(decodedTx.maxFeePerGas * decodedTx.gasLimit, 18);
				transactionDetails.value.fee = `${parseFloat(tempFee).toFixed(8)} ${selectedChain.value.ticker}`
				transactionDetails.value.feeUSD = `$${(tempFee * assetPrices.native).toFixed(2)}`;
			}
			transactionDetails.value.unsignedTx = myUnsignedTx;
		} catch (error) {
			console.error("Failed to decode unsigned transaction:", error);
			return null;
		}
	}

	const walletConnectSend = () => {
		sending.value = true;
		unsignedTx.value = walletConnectUnsignedTx;
		decodeUnsignedTx(walletConnectUnsignedTx);
		transactionDetails.value.txHash = base64ToHex(walletConnectTxHash);
		let finalTxHash = '&' + walletConnectTxHash;
		chunks = [finalTxHash];
		psbtGenerated.value = true;
		txidStr.value = '';
		transactionSubmitted.value = false;
		transactionComplete.value = false;
		signed.value = false;
		qrCodeUrl.value = chunks[0];
		qrIndex = 0;
	}

	const generatePSBT = () => {
		if (!recipientAddress.value || !amountInput.value) {
			showErrorToast("Please enter both recipient address and amount");
			return;
		}
		if (selectedFee.value === -1) {
			currentFee.value = '';
			showErrorToast("Please select a transaction fee");
			return;
		}
		if (selectedChain.value.name === 'Bitcoin') {
			let amountInSats = BigInt(Math.floor(amountInput.value*100000000));
			let amounts = [amountInSats];
			let recipients = [recipientAddress.value];
			transactionDetails.value.to = recipientAddress.value;
			transactionDetails.value.amount = amountInput.value;
			const tempAmount = parseFloat(amountInput.value);
			transactionDetails.value.amountUSD = `$${(tempAmount * assetPrices.native).toFixed(2)}`;
			transactionDetails.value.fee = currentFee.value;
			const tempFee = parseFloat(currentFee.value);
			transactionDetails.value.feeUSD = `$${(tempFee * assetPrices.native).toFixed(2)}`;
			chunks = wallet.send(recipients, amounts, BigInt(feeInSats));
			if(chunks.length > 1){
				psbtGenerated.value = true;
				qrCodeUrl.value = chunks[0];
				qrIndex = 0;
			}else{
				showErrorToast(chunks[0].replace(/^Error: /, ""));
			}
		} else {
			if (!isAddress(recipientAddress.value)) {
				showErrorToast("Invalid recipient address");
				recipientAddress.value = "";
				return;
			}
			const isNative = selectedAssetObject.value.symbol === selectedChain.value.ticker;
			const gasLimit = isNative ? 21000 : 160000;
			const rawFeeEth = wallet.estimate_fee(selectedFee.value, gasLimit);
			const feeWei = parseUnits(rawFeeEth.toString(), 18);

			const balWei = parseUnits(walletAmounts.value.native.toString(), 18);
			const sendWei = isNative ? parseUnits(amountInput.value.toString(), 18) : parseUnits(amountInput.value.toString(), selectedAssetObject.value.decimals);

			if (isNative) {
				if (balWei < feeWei) {
					showErrorToast("Insufficient funds for gas fee");
					return;
				}
				if (balWei < feeWei + sendWei) {
					showErrorToast("Insufficient funds");
					return;
				}
			} else {
				if (balWei < feeWei) {
					showErrorToast("Insufficient funds for gas fee");
					return;
				}
				if (selectedAssetObject.value.balance < parseFloat(amountInput.value)) {
					showErrorToast(`There is insufficient ${selectedAssetObject.value.symbol} to make this transaction`);
					return;
				}
			}

			currentFee.value = `${parseFloat(rawFeeEth).toFixed(8)} ${selectedChain.value.ticker}`;
			showFullUnsignedTx.value = false;
			let preChunk;
			if (isNative) {
				preChunk = wallet.send(recipientAddress.value, sendWei.toString(), selectedFee.value);
			} else {
				const amountBigInt = parseUnits(amountInput.value.toString(), selectedAssetObject.value.decimals).toString();
				const tokenAddress = selectedAssetObject.value.address;
				preChunk = wallet.erc20_transfer(tokenAddress, recipientAddress.value, amountBigInt, selectedFee.value);
			}
			let txParts = preChunk.split(":&");
			decodeUnsignedTx(txParts[0]);
			transactionDetails.value.txHash = base64ToHex(txParts[1]);
			unsignedTx.value = txParts[0];
			let finalTxHash = "&" + txParts[1];
			chunks = [finalTxHash];
			psbtGenerated.value = true;
			qrCodeUrl.value = chunks[0];
			qrIndex = 0;
		}
	}

	const continueToBroadcast = async(qrString) => {
		if (qrString.length > 0) {
			let parts = qrString.split(":");
			let sub_parts = parts[0].split("/");
			if (firstTime) {
				totalBCodes = Number(sub_parts[1]);
				broadcastStrings = new Array(totalBCodes).fill(null);
				firstTime = false;
			}
			let index = Number(sub_parts[0]);
			index--;
			if (broadcastStrings[index] == null) {
				totalScanned++;
				let pairIncrease = 100/totalBCodes;
				pairPercent += pairIncrease;
				pairPercent = Math.round(pairPercent);
				percentSynced.value = pairPercent + " %";
				broadcastStrings[index] = parts[1];
				debugInfo.value = parts[1];
			}
			if (totalScanned == totalBCodes) {
				pairPercent = 100;
				let finalString = "";
				for(let i = 0; i < broadcastStrings.length; i++){
					finalString += broadcastStrings[i];
				}
				//Handle wallet connect scenario
				if (walletConnectSign) {
					transactionSubmitted.value = true;
					transactionComplete.value = true;
					//This method broadcasta to the blockchain and has a transaction ID
					if (walletConnectMethod === 'eth_sendTransaction') {
						let result = await wallet.broadcast_eip1559(walletConnectUnsignedTx, finalString);
						console.log("result: ", result);
						if (!result.includes("Error")) {
							txidStr.value = result;
							try {
								const res = await signClient.respond({
									topic: walletConnectTopic,
									response: {
										id: walletConnectID,
										jsonrpc: '2.0',
										result: result
									}
								});
								console.log("WALLET CONNECT SUCCESS");
								broadcastStrings = [];
								firstTime = true;
								totalScanned = 0;
								walletConnectMethod = "";
							} catch (err) {
								console.error("WALLET CONNECT FAILED: ", err);
							}
						}
					}	
					//These methods do not broadcast to the blockchain and has NO transaction ID
					if (walletConnectMethod === 'eth_signTypedData_v4' || walletConnectMethod === 'personal_sign') {
						let signature = await wallet.signature_hex_from_b64(finalString);
						try {
							const res = await signClient.respond({
								topic: walletConnectTopic,
								response: {
									id: walletConnectID,
									jsonrpc: '2.0',
									result: signature
								}
							});
							console.log("WALLET CONNECT SUCCESS");
							broadcastStrings = [];
							firstTime = true;
							totalScanned = 0;
							walletConnectMethod = "";
						} catch (err) {
							console.error("WALLET CONNECT FAILED: ", err);
						}
					}
				} else {
					walletConnectMethod = "";
					transactionSubmitted.value = true;
					transactionComplete.value = true;
					let result;
					if (selectedChain.value.name == 'Bitcoin') {
						result = await wallet.broadcast(finalString);
					} else {
						let rawUnsigned = unsignedTx.value.trim();
						if (!rawUnsigned.startsWith("0x")) {
							rawUnsigned = "0x" + rawUnsigned;
						}
						unsignedTx.value = "";
						result = await wallet.broadcast(rawUnsigned, finalString);
					}
					console.log("result: ", result);
					//if (!result.includes("Error:")) {
					if (!result || result === "") {
						showErrorToast("RPC Error. Please hard refresh the web app and try again");
					} else if (!result.includes("Error:")) {
						broadcastStrings = [];
						txidStr.value = result;
						firstTime = true;
						totalScanned = 0;
						if (selectedChain.value.name == 'Bitcoin') {
							saveTrustedPending();
						}
					} else {
						showErrorToast(result.replace(/^Error: /, ""));
						backToWallet();
					}
				}
				//await callToSync();
			}
		}
	}

	const copyUnsignedTxHash = async () => {
		try {
			await navigator.clipboard.writeText(unsignedTxHash.value);
			showSuccessToast('Unsigned Tx Hash copied to clipboard');
		} catch (err) {
			showErrorToast('Failed to copy Unsigned Tx Hash');
		}
	}

	const copyUnsignedTx = async () => {
		try {
			await navigator.clipboard.writeText(unsignedTx.value);
			showSuccessToast('Unsigned Tx copied to clipboard');
		} catch (err) {
			showErrorToast('Failed to copy Unsigned Tx');
		}
	}

	const toggleSendAccountDropdown = () => {
		sendAccountDropdownOpen.value = !sendAccountDropdownOpen.value;
		assetDropdownOpen.value = false;
	}

	const selectSendAccount = (account) => {
		recipientAddress.value = account.address;
		sendAccountDropdownOpen.value = false;
	}

	const toggleAssetDropdown = () => {
		assetDropdownOpen.value = !assetDropdownOpen.value;
		sendAccountDropdownOpen.value = false;
	}

	const selectAsset = (asset) => {
		selectedAsset.value = asset.symbol;
		assetDropdownOpen.value = false;
	}

	const openRawTransactionDecoder = () => {
		window.open(`https://rawtxdecode.in/?tx=${encodeURIComponent(unsignedTx)}`, '_blank');
	}

	const onDecodeAddress = async (result) => {
		const maybeBTCAddress = result.trim();
		if (selectedChain.value.name === 'Bitcoin') {
			if (maybeBTCAddress.startsWith('bc') || maybeBTCAddress.startsWith('1')  || maybeBTCAddress.startsWith('3')) {
				recipientAddress.value = maybeBTCAddress;
				setTimeout(() => { isScanningAddress.value = false }, 500);
			} else {
				showErrorToast('Invalid address');
				recipientAddress.value = '';
				setTimeout(() => { isScanningAddress.value = false }, 3000);
			}
		} else {
			const maybeETHAddress = result.trim().toLowerCase();
			if (maybeETHAddress.startsWith('0x')) {
				recipientAddress.value = maybeETHAddress;
				setTimeout(() => { isScanningAddress.value = false }, 500);
			} else {
				showErrorToast('Invalid address');
				recipientAddress.value = '';
				setTimeout(() => { isScanningAddress.value = false }, 3000);
			}
		}
	}

	const backToPSBT = () => {
		signed.value = false;
		qrIndex = 0;
	}

	//================================================== BROADCAST ==================================================

	const nextBroadcast = () => {
		signed.value = true;
		pairPercent = 0;
		percentSynced.value = pairPercent + " %";
		pairScan = false;
		firstTime = true;
	}

	const openTxInExplorer = () => {
		if (txidStr.value != "") {
			window.open(`${blockExplorerUrl.value}/tx/${txidStr.value}`, '_blank');
		}
	}

	const abbreviateString = (str, numChars) => {
		if (str.length <= numChars * 2) {
			return str;
		}
		const start = str.substring(0, numChars);
		const end = str.substring(str.length - numChars);
		return `${start}...${end}`;
	}

	//================================================ ACCOUNT & KYC SCREEN ==================================================

	async function handleLogin({ email, password }) {
		authLoading.value = true;
		try {
			const payload = {
				email: email,
				password: password
			};
			const response = await axios.post(`${otcURL}/api/otc/login`, payload);

			isLoggedIn.value = true;
			preInitialize();
		} catch (err) {
			showErrorToast(err.response?.data?.error || 'Login failed');
		} finally {
			authLoading.value = false;
			setTimeout(() => (successMessage.value = ''), 3000);
		}
	}

	async function handleSignup({ email, password, confirmPassword, acceptedTerms }) {
		authLoading.value = true;
		try {
			const payload = {
				email: email,
				password: password,
				confirmPassword: confirmPassword,
				acceptedTerms: acceptedTerms
			};
			const response = await axios.post(`${otcURL}/api/otc/register`, payload);

			successMessage.value = 'Confirmation email sent. Please check your inbox.';
		} catch (err) {
			showErrorToast(err.response?.data?.error || 'Sign-up failed');
		} finally {
			authLoading.value = false;
			setTimeout(() => successMessage.value = '', 3000);
		}
	}

	async function handleForgotPassword(email) {
		try {
			const payload = {
				email: email
			};
			const response = await axios.post(`${otcURL}/api/otc/forgot-password`, payload);

			showSuccessToast('If you have an account, a reset password email will been sent to you');
		} catch (err) {
			showErrorToast(err.response?.data?.error || 'Failed to send reset email');
		}
	}

	async function handleResetPassword({ token, password, confirmPassword }) {
		try {
			const payload = {
				token: token,
				password: password,
				confirmPassword: confirmPassword
			};
			const response = await axios.post(`${otcURL}/api/otc/reset-password`, payload);

			showSuccessToast('Password has been reset! Please login.');
			showResetPassword.value = false;
			window.history.replaceState({}, '', window.location.pathname);
		} catch (err) {
			showErrorToast(err.response?.data?.error || 'Failed to reset password');
		}
	}

	async function fetchProfile() {
		try {
			const response = await axios.get(`${otcURL}/api/otc/get-profile`, { withCredentials: true });
			
			const { profileData } = response.data;
			const {
				email,
				name,
				username,
				status,
				rejectionReason: teamRejectionReason,
				withdrawalAddress: wAddr,
				kycData
			} = profileData;

			userProfile.value = {
				email,
				name,
				username,
				kycData: kycData || { companyName: '' }
			};

			isLoggedIn.value = true;
			kycStatus.value = status || 'Not Started';
			rejectionReason.value = teamRejectionReason || '';
			withdrawalAddress.value = wAddr ? { address: wAddr, name: '' } : null;
			isEditingProfile.value = !(name || username);
		} catch (err) {
			isLoggedIn.value = false;
		}
	}

	async function updateProfile() {
		loadingProfile.value = true;
		errorMessage.value   = '';
		try {
			const payload = {
				name: userProfile.value.name,
				username: userProfile.value.username
			}
			const response = await axios.post(`${otcURL}/api/otc/update-profile`, payload, { withCredentials: true });

			successMessage.value = 'Profile saved!';
			isEditingProfile.value = false;
			setTimeout(() => successMessage.value = '', 3000);
		} catch (err) {
			showErrorToast(err.response?.data?.error || 'Save failed');
		} finally {
			loadingProfile.value = false;
		}
	}

	function openAccountView() {
		showTradeView.value = false;
		exportZpubView.value = false;
		sending.value = false;
		showReceive.value = false;
		currentView.value = 'account';
	}

	function openWalletView() {
		showTradeView.value  = false;
		exportZpubView.value = false;
		sending.value = false;
		showReceive.value = false;
		currentView.value = 'wallet';
	}

	function depositZAR() {
		if (kycStatus.value !== 'Approved') return;
		if (selectedChain.value.name !== 'Ethereum') {
			showDepositSwitchConfirm.value = true;
		} else {
			openDepositPopup();
		}
	}

	function formatCurrency(value) {
		if (value == null || isNaN(value)) return '';
		const str = Number(value).toFixed(2);
		return 'R' + str.replace(/\d(?=(\d{3})+\.)/g, '$&,');
	}

	async function openDepositPopup() {
		await fetchDepositReference();
		showDepositPopup.value = true;
	}

	async function confirmDepositSwitch() {
		switchLoading.value = true;
		const eth = getChain('Ethereum');
		await selectChain(eth);
		highlightChain.value = true;
		setTimeout(() => (highlightChain.value = false), 3000);
		await openDepositPopup();
		switchLoading.value = false;
		showDepositSwitchConfirm.value = false;
	}

	function cancelDepositSwitch() {
		showDepositSwitchConfirm.value = false;
	}

	/*async function handleUserDeposit(withdrawAddress) {
		try {
			await axios.post('http://localhost:8080/api/otc/update-withdrawal-address', 
				{address: withdrawAddress.address, name: withdrawAddress.name},
				{ withCredentials: true }
			);

			const { data: newDeposit } = await axios.post('http://localhost:8080/api/otc/deposits',
				{ withdrawalAddress: withdrawAddress.address },  
				{ withCredentials: true }
			);
			// unwrap depositData so userDeposits is an array of { createdAt, … }
			const { data } = await axios.post(
			'http://localhost:8080/api/otc/deposits',
			{ withdrawalAddress: withdrawAddress.address },
			{ withCredentials: true }
			);
			console.log('deposit response:', data);
			console.log('depositData payload:', data.depositData);
			userDeposits.value.unshift(data.depositData);
			showConfirmDeposit.value = false;
			showDepositPopup.value = false;
			showSuccessToast('Deposit Pending. This may take up to 2 business days.');
		} catch (e) {
			showErrorToast('Failed to create deposit');
			console.error(e);
		}
	}*/

	async function handleUserDeposit(withdrawAddress) {
		try {
			const payload = {
				withdrawalAddress: withdrawAddress.address
			}
			const response = await axios.post(`${otcURL}/api/otc/create-deposit`, payload, { withCredentials: true });
			await fetchUserDeposits();
			//userDeposits.value.unshift(response.data.depositData);
			showConfirmDeposit.value = false;
			showDepositPopup.value = false;
			showSuccessToast('Deposit Pending. This may take up to 2 business days.');
		} catch (e) {
			showErrorToast('Failed to create deposit');
			console.error(e);
		}
	}

	function handleDepositNext(addr) {
		withdrawalAddress.value = addr;
		showDepositPopup.value = false;
		showConfirmDeposit.value = true;
	}

	async function confirmWithdrawalSwitch() {
		switchLoading.value = true;
		const eth = getChain('Ethereum');
		await selectChain(eth);
		highlightChain.value = true;
		setTimeout(() => (highlightChain.value = false), 3000);
		switchLoading.value = false;
		showWithdrawalSwitchConfirm.value = false;
		showWithdrawalAddressPopup.value = true;
	}

	function cancelWithdrawalSwitch() {
		showWithdrawalSwitchConfirm.value = false;
	}

	async function openWithdrawalAddress() {
		if (selectedChain.value.name !== 'Ethereum') {
			const eth = getChain('Ethereum');
			await selectChain(eth);
			highlightChain.value = true;
			setTimeout(() => (highlightChain.value = false), 3000);
		}
		showWithdrawalAddressPopup.value = true;
	}

	async function handleWithdrawalAddressConfirm(acc) {
		try {
			const payload = {
				address: acc.address,
				name: acc.name
			};
			const response = await axios.post(`${otcURL}/api/otc/update-withdrawal-address`, payload, { withCredentials: true });

			showSuccessToast('Withdrawal address saved');
			withdrawalAddress.value = { name: acc.name, address: acc.address };
			showWithdrawalAddressPopup.value = false;
		} catch (err) {
			showErrorToast(err.response?.data?.error || 'Failed to save withdrawal address');
		}
	}

	/*function abbr (addr) {
		return addr.slice(0,6) + '...' + addr.slice(-4)
	}*/

	function abbr(input) {
		// if input is an object, pull out .address, otherwise assume it's already a string
		const addr = typeof input === 'string'
		? input
		: input?.address || ''

		if (!addr) return ''
		return addr.slice(0,6) + '…' + addr.slice(-4)
	}

	const copyWithdrawalAddress = async (address) => {
		try {
			await navigator.clipboard.writeText(address);
			showSuccessToast('Address copied');
		} catch {
			showErrorToast('Failed to copy address');
		}
	}

	function cycleKycStatus() {
		const order = ['Not Started','Pending','Approved','Rejected'];
		let idx = order.indexOf(kycStatus.value) + 1;
		if (idx >= order.length) idx = 0;
		kycStatus.value = order[idx];
	}

	async function submitKyc(form) {
		kycSubmitting.value = true;
		try {
			const data = new FormData();
			data.append('companyName', form.companyName);
			data.append('registrationNumber', form.registrationNumber);
			data.append('companyAddress', form.companyAddress);
			data.append('sourceOfFunds', form.sourceOfFunds);
			data.append('intendedPurpose', form.intendedPurpose);
			data.append('natureOfBusiness', form.natureOfBusiness);

			// serialize the non–file director/UBO info
			data.append('directors', JSON.stringify(
			form.directors.map(d => ({ name: d.name }))
		));
		data.append('ubos', JSON.stringify(
			form.ubos.map(u => ({ name: u.name, percentage: u.percentage }))
		));

		form.directors.forEach(d => data.append('directors', d.idFile));
		form.ubos.forEach(u => data.append('ubos', u.idFile));

		await axios.post(`${otcURL}/api/submit-kyc`, data, { headers: { 'Content-Type': 'multipart/form-data' } }, { withCredentials: true });

		successMessage.value = 'KYC submitted! Status is now Pending.';
		kycStatus.value   = 'Pending';
		showKycForm.value = false;
		setTimeout(() => (successMessage.value = ''), 3000);
		//await fetchProfile();
		} catch (err) {
			errorMessage.value = err.response?.data?.error || 'Submission failed';
			setTimeout(() => (errorMessage.value = ''), 3000);
		} finally {
			kycSubmitting.value = false;
		}
	}

	function handleAddAccount() {
		showAddAccountPopup.value = true;
	}

	function addOperation({ type, amount, address }) {
		const date = new Date().toISOString().split('T')[0]; 
			operations.value.unshift({
			date,
			type,
			amount,
			status: 'Pending',
			withdrawalAddress: address
		});
	}

	async function tradeInDeFi() {
		if (selectedChain.value.name !== 'Bitcoin') {
			openTradeView();
			return;
		}
		const eth = getChain('Ethereum');
		await selectChain(eth);
		openTradeView();
	}

	function handleTradeClick() {
		if (selectedChain.value.name === 'Bitcoin') {
			showTradeConfirm.value = true;
		} else {
			tradeInDeFi();
		}
	}

	async function confirmTrade() {
		switchLoading.value = true;
		await tradeInDeFi();
		switchLoading.value = false;
		showTradeConfirm.value = false;
	}

	function cancelTrade() {
		showTradeConfirm.value = false;
	}

	//============================================= DEPOSITS =================================================

	async function fetchDepositReference() {
		try {
			const payload = {
				currency: 'ZAR'
			};
			const response = await axios.post(`${otcURL}/api/otc/deposit-reference`, payload, { withCredentials: true });

			depositReference.value = response.data.reference;
		} catch (err) {
			showErrorToast('Unable to get deposit reference');
			console.error(err);
		}
	}

	async function fetchUserDeposits() {
		try {
			const response = await axios.get(`${otcURL}/api/otc/get-deposits`, { withCredentials: true });

			userDeposits.value = response.data.depositsData;
		} catch (e) {
			console.error('Failed to load user deposits', e);
		}
	}

	//================================================== CHAINS ==================================================

	const toggleChainDropdown = () => {
		if (switchingChain.value) { return; }
		chainDropdownOpen.value = !chainDropdownOpen.value;
		accountDropdownOpen.value = false;
		showAddTokenPopup.value = false;
		tokenContractDropdownOpen.value = false;
		assetMenuOpen.value = false;
	}

	const selectChain = async (chain) => {
		// 1) Prevent any new switch while one is already in progress
		if (switchingChain.value || isSyncing.value || isBalanceLoading.value) {
			return;
		}
		isBalanceLoading.value = true;
		if (chain.name === "Add Network") {
			showAddNetworkPopup.value = true;
			isBalanceLoading.value = false;
		} else {
			switchingChain.value = true;
			if (selectedChain.value.name === 'Bitcoin') {
				walletAddress.value = '';
			}
			walletAmounts.value = { native: 0, erc20: {} }
			allTransactions.value = [];
			selectedChain.value = chain;
			selectedAsset.value = chain.ticker;
			walletPrefs.value.selectedChain = selectedChain.value.name;
			localStorage.setItem('cardware_prefs', JSON.stringify(walletPrefs.value));
			chainDropdownOpen.value = false;
			showUnconf.value = false;
			psbtGenerated.value = false;
			recipientAddress.value = '';
			amountInput.value = '';
			currentFee.value = '';
			setMaxAmount.value = ''
			selectedFee.value = -1;
			isMobileMenuOpen.value = false;
			showTradeView.value = false;
			chainDropdownOpen.value = false;
			initializeAccounts();
			initializeTokens();
			await fetchPrices();
			const xpub = localStorage.getItem('cardware_xpub');
			if (paired.value && xpub) {
				await initializeWallet(xpub, "m/0/0");
			} 
		}
	}

	const getChain = (chainName) => {
		return chains.value.find(function(chain) {
			return chain.name === chainName;
		});
	}

	const addNetwork = (network) => {
		walletPrefs.value.preferredChains.push(network.name);
		localStorage.setItem('cardware_prefs', JSON.stringify(walletPrefs.value));
		showAddNetworkPopup.value = false;
		walletTokens.value[network.name] = { erc20: [] };
		localStorage.setItem('cardware_tokens', JSON.stringify(walletTokens.value));
		showAddNetworkPopup.value = false;
		selectChain(network);
	}

	const closeNetworkPopup = () => {
		showAddNetworkPopup.value = false;
		chainDropdownOpen.value = false;
	}

	//================================================== ACCOUNTS ==================================================

	const toggleAccountDropdown = () => {
		accountDropdownOpen.value = !accountDropdownOpen.value;
		chainDropdownOpen.value = false;
		tokenContractDropdownOpen.value = false;
		assetMenuOpen.value = false;
	}

	const openAddAccountPopup = () => {
		newAccountName.value = '';
		showAddAccountPopup.value = true;
	}

	const selectAccount = async (account) => {
		isBalanceLoading.value = true;
		allTransactions.value = [];
		selectedAccount.value = account;
		accountDropdownOpen.value = false;
		walletPrefs.value.selectedAccount = account.derivationPath;
		localStorage.setItem('cardware_prefs', JSON.stringify(walletPrefs.value));
		const xpub = localStorage.getItem('cardware_xpub');
		if (xpub) {
			if (selectedChain.value.name === 'Bitcoin') {
				await initializeWallet(xpub, "m/0/0");
			} else {
				await initializeWallet(xpub, account.derivationPath);
				const newAddr = walletAddress.value;
				const chainId = selectedChain.value.chainId;
				//await updateWalletConnect(newAddr, chainId);
			}
		} else {
			unpairWallet();
		}
	}

	const addAccount = async () => {
		if (selectedChain.value.name === 'Bitcoin') {
			showErrorToast("Adding accounts is not supported for Bitcoin");
			return;
		}
		const totalAccounts = walletAccounts.value[selectedChain.value.name].length;
		const newDerivationPath = `m/${totalAccounts}/0`;
		const xpub = localStorage.getItem('cardware_xpub');
		if (!xpub) {
			showErrorToast("No extended public key found");
			return;
		}
		accountDropdownOpen.value = false;
		showAddAccountPopup.value = false;
		const defaultName = `Account ${ totalAccounts + 1 }`;
		const name = newAccountName.value.trim() || defaultName;
		const newAccount = {
			name,
			address: '',
			derivationPath: newDerivationPath
		};
		walletAccounts.value[selectedChain.value.name].push(newAccount);
		localStorage.setItem('cardware_accounts',JSON.stringify(walletAccounts.value));
		walletPrefs.value.selectedAccount = newDerivationPath;
		localStorage.setItem('cardware_prefs', JSON.stringify(walletPrefs.value));
		newAccountName.value = '';
		await initializeWallet(xpub, newDerivationPath);
	}

	//============================================= ADD TOKEN CONTRACT ==============================================

	const resetContractInput = () => {
		tokenAddress.value = '';
		tokenDecimals.value = '';
		tokenTicker.value = '';
		tokenName.value = '';
	}

	const openAddTokenPopup = () => {
		showAddTokenPopup.value = true;
		chainDropdownOpen.value = false;
		accountDropdownOpen.value = false;
		assetMenuOpen.value = false;
		resetContractInput();
	}

	const cancelAddTokenPopup = () => {
		showAddTokenPopup.value = false;
		tokenContractDropdownOpen.value = false;
		tokenPopupMode.value = 'auto';
		resetContractInput();
	}

	const canRemove = asset => {
		const blocked = [selectedChain.value.ticker, 'USDT', 'USDC'];
		return !blocked.includes(asset.symbol);
	}

	const openAssetMenu = (addr) => {
		assetMenuOpen.value = assetMenuOpen.value === addr ? null : addr;
		tokenContractDropdownOpen.value = false;
	}

	const onSelectSend = (asset) => {
		assetMenuOpen.value = null;
		selectedAsset.value = asset.symbol;
		startSend();
	}

	const onSelectReceive = (asset) => {
		assetMenuOpen.value = null;
		selectedAsset.value = asset.symbol;
		showReceiveView();
	}

	const onSelectRemove = (asset) => {
		assetMenuOpen.value  = null;
		pendingRemoveAddr.value = asset.address;
		showRemoveConfirm.value = true;
	}

	const confirmRemove = async () => {
		const chain = selectedChain.value.name;
		walletTokens.value[chain].erc20 = walletTokens.value[chain].erc20.filter(t => t.address !== pendingRemoveAddr.value);
		delete tokenIcons.value[pendingRemoveAddr.value];
		localStorage.setItem('cardware_tokens', JSON.stringify(walletTokens.value));
		localStorage.setItem('cardware_token_icons', JSON.stringify(tokenIcons.value));
		initializeTokens();
		fetchPrices();
		await callToSync();
		showRemoveConfirm.value = false;
		pendingRemoveAddr.value = null;
	}

	const cancelRemove = () => {
		showRemoveConfirm.value = false;
		pendingRemoveAddr.value = null;
	}

	const checkIfContractAddress = async (address) => {
		try {
			const provider = wallet?.provider || new JsonRpcProvider(esploraUrl.value);
			const code = await provider.getCode(address);
			return code && code !== '0x';
		} catch {
			return false;
		}
	}

	const getTokenData = async (addr) => {
		if (tokenPopupMode.value === 'auto') {
			const result = await wallet.validate_contract(addr)
			.catch(err => { throw new Error(`Contract validation error: ${err.message}`) });
			const data = (typeof result === 'object') ? result : JSON.parse(result);
			const { name, symbol, decimals } = data;
		if (!name || !symbol || decimals == null) {
			throw new Error('Invalid token contract');
		}
			return { name, symbol, decimals };
		} else {
			if (!tokenName.value || !tokenTicker.value || !tokenDecimals.value) {
				errorMessage('All fields are required in custom mode');
			}
			const decimals = Number(tokenDecimals.value);
			if (isNaN(decimals)) throw new Error('Decimals must be a valid number');
			if (!await checkIfContractAddress(addr)) throw new Error('Address is not a valid contract');
			return { name: tokenName.value, symbol: tokenTicker.value, decimals };
		}
	}

	const getIconURL = (addr) => `${process.env.BASE_URL}token-icons/${selectedChain.value.slug}/${addr}/logo.png`;

	const fetchIcon = async (addr) => {
		try {
			await axios.head(getIconURL(addr));
			return getIconURL(addr.toLowerCase());
		} catch {
			const { icon: remoteIcon } = await fetchTokenData(addr).catch(() => ({ icon: placeholderIcon }));
			return remoteIcon || placeholderIcon;
		}
	}

	const fetchTokenData = async (contractAddress) => {
		try {
			const payload = {
				slug: selectedChain.value.slug,
				contractAddress: contractAddress
			};
			const response = await axios.post(`${baseURL}/api/get-token`, payload);
			const tokenData = {
				icon: response.data.icon || placeholderIcon,
				coingeckoId: response.data.coingeckoId,
				platform: response.data.asset_platform_id
			};
			return tokenData;
		} catch (err) {
			console.error('fetchTokenData error:', err);
			return { icon: placeholderIcon, coingeckoId: null, platform: null };
		}
	}

	const addTokenToStore = (addr, { name, symbol, decimals, icon }) => {
		const chain = selectedChain.value.name;
		tokenIcons.value[addr] = icon;
		walletTokens.value[chain] = walletTokens.value[chain] || { erc20: [] };
		walletTokens.value[chain].erc20.push({ address: addr, name, symbol, decimals, icon });
		localStorage.setItem('cardware_tokens', JSON.stringify(walletTokens.value));
		localStorage.setItem('cardware_token_icons', JSON.stringify(tokenIcons.value));
	}

	const finalizeAdd = () => {
		initializeTokens();
		startSync();
		showAddTokenPopup.value = false;
		tokenContractDropdownOpen.value = false;
		tokenPopupMode.value = 'auto';
		showSuccessToast('Added Token Contract Successfully');
		resetContractInput();
	}

	const validateTokenContract = async () => {
		if (isValidating.value) return;
			const addr = tokenAddress.value.trim().toLowerCase();
		if (!addr) return showErrorToast('Please enter a contract address');
		if (walletTokens.value[selectedChain.value.name]?.erc20.some(t => t.address === addr)) {
			return showErrorToast('Token already added');
		}
		isValidating.value = true;
		try {
			const { name, symbol, decimals } = await getTokenData(addr);
			const icon = await fetchIcon(addr);
			addTokenToStore(addr, { name, symbol, decimals, icon });
			finalizeAdd();
		} catch (err) {
			showErrorToast(err.message);
		} finally {
			isValidating.value = false;
		}
	}

	const importTokenPreferences = async () => {
		if (defaultTokensImported.value) {
			return showErrorToast('Default tokens already imported');
		}
		const chain = selectedChain.value.name;
		walletTokens.value[chain] = walletTokens.value[chain] || { erc20: [] };

		defaultTokens.forEach(t => {
			const addr = t.address.toLowerCase();
			if (!walletTokens.value[chain].erc20.some(x => x.address === addr)) {
				const icon = getIconURL(addr);
				walletTokens.value[chain].erc20.push({ ...t, icon });
				tokenIcons.value[addr] = icon;
			}
		});
		localStorage.setItem('cardware_tokens', JSON.stringify(walletTokens.value));
		localStorage.setItem('cardware_token_icons', JSON.stringify(tokenIcons.value));
		localStorage.setItem('cardware_default_tokens_imported', 'true');
		defaultTokensImported.value = true;
		showSuccessToast('Default tokens imported');
		initializeTokens();
		await callToSync();
		showAddTokenPopup.value = false;
		tokenContractDropdownOpen.value = false;
	}

	//============================================ ADDRESS BOOK  ===================================================

	const toggleAddressBookPopup = () => {
		showAddressBookPopup.value = !showAddressBookPopup.value;
	}

	const addAddress = () => {
		const name = newContactName.value.trim();
		const addr = newContactAddress.value.trim();
		if (!name || !addr) {
			return showErrorToast('Both name and address are required');
		}
		addressBook.value.push({ name, address: addr });
		newContactName.value    = '';
		newContactAddress.value = '';
		addingContact.value = false;
	}

	const toggleAddressMenu = () => {
		addressMenuOpen.value = !addressMenuOpen.value;
		sendAccountDropdownOpen.value = false;
	}

	const selectBookAddress = (entry) => {
		recipientAddress.value = entry.address;
		showAddressBookPopup.value = false;
	}

	//================================================= PRICES ===================================================

	const fetchPrices = async () => {
		priceError.value = '';
		const slug = selectedChain.value.slug;
		const contracts = (currentTokens.value?.erc20 || [])
			.map(t => t.address.toLowerCase())
			.join(',');

		try {
			const payload = {
				chain: slug,
				contracts: contracts
		};
		const response = await axios.post(`${baseURL}/api/get-prices`, payload);
		assetPrices.native = response.data.native;
		assetPrices.tokens = response.data.tokens || {};

		} catch (error) {
			//showErrorToast('Failed to load prices');
			console.error('Price fetch failed:', error);
		}
	}

	//================================================== MOBILE ==================================================

	const toggleMobileMenu = () => {
		isMobileMenuOpen.value = true;
		chainDropdownOpen.value = false;
		tokenContractDropdownOpen.value = false;
	}

	const closeMobileMenu = () => {
		isMobileMenuOpen.value = false;
		chainDropdownOpen.value = false;
	}

	const showExportZpubView = () => {
		isMobileMenuOpen.value = false;
		exportZpubView.value = true;  
		showTradeView.value = false;
	}

	const copyZpub = async () => {
		try {
			await navigator.clipboard.writeText(zpub.value);
			showSuccessToast('zPub copied to clipboard');
		} catch (err) {
			showErrorToast('Failed to copy zPub');
		}
	}

	const toggleBalance = () => {
		showBalance.value = !showBalance.value;
		assetMenuOpen.value = false;
	}

	//================================================== CAMERA ==================================================

	const onInit = (promise) => {
		promise.then(() => {
			isCameraActive.value = true;
		}).catch((error) => {
			console.error(error);
		});
	}

	const onDecode = async(result) => {
		if (pairScan) {
			await continueToPair(result);
		} else {
			await continueToBroadcast(result);
		}
	}

	//================================================== QR CODES ==================================================
  
	const nextQR = () => {
		qrIndex++;
		if(qrIndex == chunks.length){
			qrIndex = 0;
		}
		qrCodeUrl.value = chunks[qrIndex];
	}

	const prevQR = () => {
		qrIndex--;
		if(qrIndex == -1){
			qrIndex = chunks.length - 1;
		}
		qrCodeUrl.value = chunks[qrIndex];
	}

	//================================================== ERRORS ==================================================

	const showSuccessToast = (msg) => {
		successMessage.value = msg;
		setTimeout(() => { successMessage.value = ''; }, 3000);
	}

	const showErrorToast = (msg) => {
		errorMessage.value = msg;
		setTimeout(() => { errorMessage.value = ''; }, 3000);
	}

	function showInfoToast(msg) {
		infoMessage.value = msg
		setTimeout(() => { infoMessage.value = '' }, 3000)
	}

	//================================================== UTILITIES ==================================================

	const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

	const showComingSoonMessage = () => {
		showComingSoonPopup.value = true;
		setTimeout(() => { successMessage.value = ""; }, 3000);
	}

	const openBlockExplorer = () => {
		if (walletAddress.value) {
			window.open(`${blockExplorerUrl.value}/address/${walletAddress.value}`, '_blank');
		}
	}

	const toggleTokenContractDropdown = () => {
		tokenContractDropdownOpen.value = !tokenContractDropdownOpen.value;
		chainDropdownOpen.value = false;
	}

	const toggleFullTx = () => {
		showFullUnsignedTx.value = !showFullUnsignedTx.value
	}

	//================================================== WATCH ==================================================
	
	watch(paired, (newVal) => {
		if (newVal) {
			currentView.value = 'account';
		}
	});

	watch(addressBook, (list) => {
		localStorage.setItem('cardware_address_book', JSON.stringify(list));
		if (list.length > 0) addingContact.value = false;
	}, { deep: true });

	watch(currentTheme, (newVal) => {
		walletPrefs.value.theme = newVal;
		localStorage.setItem('cardware_prefs', JSON.stringify(walletPrefs.value));
	});

</script>


<style scoped>
	.loader {
		border: 8px solid #f3f3f3;
		border-top: 8px solid var(--accent-color);
		border-radius: 50%;
		width: 28px;
		height: 28px;
		animation: spin 1s linear infinite;
	}

	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}

	@keyframes fade-in {
		from {
			opacity: 0;
			transform: scale(0.95);
		}
		to {
			opacity: 1;
			transform: scale(1);
		}
	}

	.animate-fade-in {
		animation: fade-in 0.5s ease-out forwards;
	}

	@keyframes scale-in {
		from {
			opacity: 0;
			transform: scale(0.8);
		}
	to {
			opacity: 1;
			transform: scale(1);
		}
	}

	.animate-scale-in {
		animation: scale-in 0.5s ease-out forwards;
	}

	@keyframes spin-slow {
		0% {
			transform: rotate(0deg);
		}
		100% {
			transform: rotate(360deg);
		}
	}

	hr {
		border-color: var(--border-color);
	}

	.digit {
		--index: 0;
	}

	.digit-flicker-enter-active {
		display: inline-block;
		animation: digitFlicker 0.4s ease-out forwards;
		animation-delay: calc(var(--index) * 0.06s);
	}

	.digit-flicker-enter-from{
		opacity: 0;
		transform: scale(1.3) rotate(5deg);
		filter: blur(3px);
	}

	@keyframes digitFlicker {
		0% {
			opacity: 0.3;
			transform: scale(1.3) rotate(5deg);
			filter: blur(3px);
		}
		20% {
			opacity: 0.5;
			transform: scale(1.2) rotate(-3deg);
			filter: blur(2px);
		}
		40% {
			opacity: 0.85;
			transform: scale(1.1) rotate(2deg);
			filter: blur(2px);
		}
		60% {
			opacity: 1;
			transform: scale(0.9) rotate(-3deg);
			filter: blur(1px);
		}
		80% {
			opacity: 1;
			transform: scale(1.02) rotate(1deg);
			filter: blur(0.5px);
		}
		100% {
			opacity: 1;
			transform: scale(1) rotate(0deg);
			filter: blur(0);
		}
	}

	.no-flicker-enter-active{
		transition: none;
	}

	.lightTheme-bg {
		background: var(--bg-color);
	}

	.pair-bg {
		background: var(--main-bg-color);
	}

	.account-bg {
		background: var(--account-block-bg-2-color);
	}

	.sidemenu-bg {
		background: var(--sidemenu-bg-color);
	}

	.mobile-header-bg {
		background: var(--mobile-header-bg-color);
	}

	.balance-bg {
		background: var(--balance-bg-color);
	}

	.popup-bg {
		background: var(--popup-bg-color);
	}

	.horizontal-line-bg {
		background: var(--line-gradient-color);
	}

	.custom-scrollbar {
		/* firefox */
		overflow-y: auto;
		scrollbar-width: thin;
		scrollbar-color: #888 #f1f1f1;
	}

	/* Chrome, Edge, and Safari */
	.custom-scrollbar::-webkit-scrollbar {
		width: 8px;                        
	}

	.custom-scrollbar::-webkit-scrollbar-track {
		background: #f1f1f1;
		border-radius: 16px;
		margin-top: 5%;
		margin-bottom: 5%;
	}

	.custom-scrollbar::-webkit-scrollbar-thumb {
		background-color: #888;            
		border-radius: 8px;
	}

	.custom-scrollbar::-webkit-scrollbar-thumb:hover {
		background-color: #555;           
	}

	.fade-enter-active, .fade-leave-active {
		transition: opacity 0.5s;
	}
	.fade-enter-from, .fade-leave-to {
		opacity: 0;
	}

	input, textarea, select {
		font-size: 16px;
	}

	input[type="number"]::-webkit-outer-spin-button,
	input[type="number"]::-webkit-inner-spin-button {
		-webkit-appearance: none;
		-moz-appearance: none;
		appearance: none;
	}

	input[type="number"] {
		-moz-appearance: none;
	}

	.fade-enter-active, .fade-leave-active {
		transition: opacity 0.2s ease-in-out;
	}
	.fade-enter-from, .fade-leave-to {
		opacity: 0;
	}

	@keyframes scaleIn {
		from {
			opacity: 0;
			transform: scale(0.9);
		}
		to {
			opacity: 1;
			transform: scale(1);
		}
	}
	.animate-scaleIn {
		animation: scaleIn 0.7s ease-out forwards;
	}

	@keyframes slowSpin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}
	.animate-slowSpin {
		animation: slowSpin 20s linear infinite;
	}

	@keyframes circleDraw {
		to {
			stroke-dashoffset: 0;
		}
	}
	.animate-circle-draw {
		animation: circleDraw 1.2s ease-out forwards;
	}

	@keyframes checkDraw {
		to {
			stroke-dashoffset: 0;
		}
	}
	.animate-check-draw {
		animation: checkDraw 0.8s ease-out forwards;
	}

	.popup-swap-enter-active,
	.popup-swap-leave-active {
		transition: transform 0.5s cubic-bezier(0.25, 1, 0.5, 1), opacity 0.5s ease;
	}
	.popup-swap-enter-from {
		transform: rotateX(-25deg) translateY(40px) scale(0.9);
		opacity: 0;
	}
	.popup-swap-enter-to {
		transform: rotateX(0) translateY(0) scale(1);
		opacity: 1;
	}
	.popup-swap-leave-from {
		transform: rotateX(0) translateY(0) scale(1);
		opacity: 1;
	}
	.popup-swap-leave-to {
		transform: rotateX(25deg) translateY(-40px) scale(0.9);
		opacity: 0;
	}

	@keyframes waveBlur {
		0%   { filter: blur(6px); opacity: 0.4; }
		50%  { filter: blur(2px); opacity: 0.8; }
		100% { filter: blur(0);   opacity: 1;   }
	}
	.wave-blur-enter-active {
		display: inline-block;
		animation: waveBlur 0.8s ease-out forwards;
		animation-delay: calc(var(--index) * 0.04s);
	}
	.wave-blur-enter-from {
		filter: blur(8px);
		opacity: 0;
	}
	.wave-blur-leave-active {
		animation: none !important;
		transition: none !important;
	}
	.wave-blur-leave-to {
		filter: blur(0);
		opacity: 0;
	}
	.digit {
		--index: 0;
		display: inline-block;
	}

	.carousel-track {
		animation: scroll-left 12s linear infinite;
	}
	@keyframes scroll-left {
		0%   { transform: translateX(0); }
		100% { transform: translateX(-50%); }
	}

	/* in your <style scoped> */

	@keyframes smooth-heavy-blur {
		0%, 100% {
			opacity: 0.6;
			filter: blur(16px);
		}
		50% {
			opacity: 0.8;
			filter: blur(10px);
		}
	}

	.animate-smooth-heavy-blur {
		animation: smooth-heavy-blur 2s ease-in-out infinite;
	}

	/* style scoped */
	.dropdown-transition-enter-active,
	.dropdown-transition-leave-active {
		transition: opacity 10ms ease, transform 10ms ease;
	}

	.dropdown-transition-enter-from,
	.dropdown-transition-leave-to {
		opacity: 0;
		transform: scale(0.95);
	}

	.dropdown-transition-enter-to,
	.dropdown-transition-leave-from {
		opacity: 1;
		transform: scale(1);
	}

	.expand-enter-active,
	.expand-leave-active {
		transform-origin: top;
		transition:
	transform 0.3s ease-out,
	opacity   0.3s ease-out;
	}
	.expand-enter-from,
	.expand-leave-to {
		transform: scaleY(0);
		opacity:   0;
	}
	.expand-enter-to,
	.expand-leave-from {
		transform: scaleY(1);
		opacity:   1;
	}


</style>